import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Activity, AlertTriangle, ArrowDown, ArrowUp, Banknote, ChevronDown,
  ClipboardCheck, Eye, Globe, Images, ListOrdered, Loader2, PlugZap, Radio,
  RefreshCw, Save, Trophy, Upload, Wand2, Zap,
} from 'lucide-react';
import client, { resolveApiAssetUrl } from '../lib/api';
import { UPLOAD_MAX_LABEL, uploadTooLarge } from '../lib/mediaAsset';
import { useToast } from '../hooks/use-toast';
import { notifySettingsUpdated } from '../context/SettingsContext';
import SportsOddsOverrides from './SportsOddsOverrides';
import { BETBY_LAYOUTS, BETBY_COLORS } from '../constants/betby';
// Varsayılan logo eşlemesi oyuncu tarafıyla **ortaktır**: panelin önizlemesi
// ile şeritte çizilen görsel ayrışırsa yönetici yüklemediği bir logoyu
// panelde göremez ya da tersi olur.
import { defaultSportsbookLogo } from '../lib/sportsbookProviders';
import { FLEXRIX_THEMES, FLEXRIX_JWT_ALGORITHMS } from '../constants/flexrix';
import {
  EMSPORTS_THEMES, EMSPORTS_JWT_ALGORITHMS, EMSPORTS_LANGUAGES,
} from '../constants/emsports';
import {
  SPORTDIGI_THEMES, SPORTDIGI_JWT_ALGORITHMS, SPORTDIGI_LANGUAGES,
} from '../constants/sportdigi';

const MASKED_SECRET = '••••••••••••••••';
export const SETTING_KEYS = [
  'sports_enabled',
  // Misafir görünümü bütün spor kaynakları için tek ayardır: sitenin
  // kendi beslemesi, Betby ve Flexrix aynı kapıyı okur.
  'sports_guest_view_enabled',
  // Betby spor kitabı (SynCode). Ayarların tek yeri burasıdır: sağlayıcı bir
  // oran kaynağı değil hazır bir sportsbook'tur ve açıldığında sitenin kendi
  // beslemesinin yerine geçer — kararın verildiği ekran da bu.
  'syncode_enabled',
  'syncode_sports_enabled',
  'syncode_api_url',
  'syncode_agent_code',
  'syncode_token',
  'syncode_currency',
  'syncode_betby_layout',
  'syncode_betby_color',
  'syncode_home_url',
  'syncode_callback_registered',
  // Flexrix Sportsbook (native). SynCode ile aynı gerekçeyle burada:
  // hazır bir sportsbook'tur ve açıldığında sitenin kendi beslemesinin
  // yerine geçer. SSO özel anahtarı bu listede **yoktur** — o yalnızca
  // "Anahtar Üret" düğmesiyle yazılır (PATCH gövdesi kabul etmez).
  'flexrix_enabled',
  'flexrix_sports_enabled',
  'flexrix_api_url',
  'flexrix_merchant_key',
  'flexrix_api_secret',
  'flexrix_sports_url',
  'flexrix_currency',
  'flexrix_jwt_algorithm',
  'flexrix_webhook_secret',
  'flexrix_freebet_enabled',
  'flexrix_callback_registered',
  'apifootball_enabled',
  'apifootball_api_key',
  'sportsbook_api_enabled',
  'sportsbook_api_key',
  'sportsbook_api_host',
  'sportsbook_api_url',
  'sportsbook_margin',
  'sportsbook_sport_order',
  'sportsbook_competition_order',
  'sportsbook_upcoming_days',
  'oddsapi_enabled',
  'oddsapi_api_key',
  'oddsapi_regions',
  'oddsapi_markets',
  'oddsapi_margin',
  'oddsapi_competition_order',
  'oddsapi_upcoming_days',
  'oddsapi_auto_settle',
  'cashout_enabled',
  'cashout_margin',
];
// Bölüm başına kaydedilecek ayar anahtarları. Her sağlayıcı bölümü kendi
// Kaydet düğmesiyle yalnızca kendi alanlarını yazar; alttaki şerit hepsini
// birden kaydetmeye devam eder. Liste prefix'ten türetilmez — sıralama
// alanları kaynağın değil "Spor ve Lig Sıralaması" bölümünün alanıdır.
export const SECTION_SETTING_KEYS = {
  flexrix: [
    'flexrix_enabled', 'flexrix_sports_enabled', 'flexrix_api_url',
    'flexrix_merchant_key', 'flexrix_api_secret', 'flexrix_sports_url',
    'flexrix_currency', 'flexrix_jwt_algorithm', 'flexrix_webhook_secret',
    'flexrix_freebet_enabled', 'flexrix_callback_registered',
  ],
  betby: [
    'syncode_enabled', 'syncode_sports_enabled', 'syncode_api_url',
    'syncode_agent_code', 'syncode_token', 'syncode_currency',
    'syncode_betby_layout', 'syncode_betby_color', 'syncode_home_url',
    'syncode_callback_registered',
  ],
  apifootball: ['apifootball_enabled', 'apifootball_api_key'],
  sportsbook: [
    'sportsbook_api_enabled', 'sportsbook_api_key', 'sportsbook_api_host',
    'sportsbook_api_url', 'sportsbook_margin', 'sportsbook_upcoming_days',
  ],
  oddsapi: [
    'oddsapi_enabled', 'oddsapi_api_key', 'oddsapi_regions', 'oddsapi_markets',
    'oddsapi_margin', 'oddsapi_upcoming_days', 'oddsapi_auto_settle',
  ],
  cashout: ['cashout_enabled', 'cashout_margin'],
  order: [
    'sportsbook_sport_order', 'sportsbook_competition_order',
    'oddsapi_competition_order',
  ],
};

// Spor bölümünün ana anahtarları hiçbir bölüme ait değildir: panelin
// üstünde dururlar ve alttaki şeritle kaydedilirler.
export const GLOBAL_ONLY_SETTING_KEYS = ['sports_enabled', 'sports_guest_view_enabled'];

// Anahtarlar sunucudan hiç dönmez; yalnızca `<alan>_configured` bayrağı gelir.
// Dolu anahtar maskeyle gösterilir ve maske aynen kalırsa isteğe konmaz.
const SECRET_KEYS = [
  'apifootball_api_key', 'sportsbook_api_key', 'oddsapi_api_key',
  'syncode_token', 'flexrix_api_secret', 'flexrix_webhook_secret',
];

// Konsensüs oran kaynakları. Lig sıralaması kaynağa göre ayrı ayarda tutulur
// (kodları farklı biçimdedir: 'EPL' / 'soccer_epl'); spor dalı sıralaması
// ikisinde ortaktır.
const FEED_LABELS = {
  sportsbook: 'Sportsbook API',
  oddsapi: 'The Odds API',
};
const FEED_ORDER_KEYS = {
  sportsbook: 'sportsbook_competition_order',
  oddsapi: 'oddsapi_competition_order',
};

// The Odds API'de bir olay listesi isteği `market × bölge` kredi harcar; bu
// yüzden iki liste de panelde açıkça yönetilir ve maliyet ekranda yazar.
const ODDSAPI_REGIONS = [
  { code: 'eu', label: 'Avrupa' },
  { code: 'uk', label: 'İngiltere' },
  { code: 'us', label: 'ABD' },
  { code: 'us2', label: 'ABD (2)' },
  { code: 'au', label: 'Avustralya' },
];
const ODDSAPI_MARKETS = [
  { code: 'h2h', label: 'Maç Sonucu' },
  { code: 'spreads', label: 'Handikap' },
  { code: 'totals', label: 'Toplam Sayı' },
];

// EveryMatrix'in ayarları `settings` kaydında **değildir**: o tablo InnoDB'nin
// satır sınırına dayanmıştır ve yeni bir metin kolonu kurulumları açılışta
// düşürür (bkz. backend/services/provider_config.py). Gövde kendi ucundan
// okunup kendi ucuna yazılır; alanlar bu yüzden ayrı bir listede durur.
const EMSPORTS_KEYS = [
  'enabled', 'sports_enabled', 'merchant_key', 'api_secret', 'sports_url',
  'currency', 'language', 'theme', 'full_width', 'jwt_algorithm',
  'callback_registered', 'freebet_enabled', 'label', 'logo_url',
];

// Digitain de aynı sebeple `provider_configs` satırındadır ve kendi ucundan
// okunup kendi ucuna yazılır. Alan kümesi EveryMatrix'inkiyle **aynı
// değildir**: dil kapalı bir kümedir (sağlayıcı on dil tanır, listedışı
// değer markanın varsayılanına düşer) ve tam genişlik ayarı sağlayıcının
// kendi panelindedir. Tema burada yazılmaz: sağlayıcıya ayrı bir uçla
// uygulanır (`PATCH /admin/sportdigi/theme`).
const SPORTDIGI_KEYS = [
  'enabled', 'sports_enabled', 'merchant_key', 'api_secret', 'sports_url',
  'currency', 'language', 'return_path', 'jwt_algorithm',
  'callback_registered', 'freebet_enabled',
];

// Exestruct da `provider_configs` satırındadır. İki sırrı vardır: API Key
// (SSO ve bahis geçmişi çağrıları) ve API Secret (gelen webhook'un imzası);
// ikisi de panele geri dönmez, yalnızca "kayıtlı" işareti gelir.
const EXESTRUCT_KEYS = [
  'enabled', 'sports_enabled', 'api_url', 'agent_code', 'api_key',
  'api_secret', 'currency', 'language', 'odd_type', 'callback_registered',
];
const EXESTRUCT_SECRETS = ['api_key', 'api_secret'];
const EXESTRUCT_LANGUAGES = [
  { value: 'tr', label: 'Türkçe' },
  { value: 'en', label: 'English' },
  { value: 'ru', label: 'Русский' },
  { value: 'fr', label: 'Français' },
  { value: 'es', label: 'Español' },
  { value: 'pt', label: 'Português' },
  { value: 'ar', label: 'العربية' },
];
// Viper GGR de `provider_configs` satırındadır. Tek sırrı API Secret'tır: hem
// bizden giden çağrıyı hem gelen cüzdan bildirimini imzalar ve panele geri
// dönmez. API Key sır değildir (sağlayıcı onu belgede açıkça gösterir).
// Spor kitabı katalogdan seçilir: kimliği bir UUID'dir ve sağlayıcı panelinde
// görünmez.
const VIPER_KEYS = [
  'enabled', 'sports_enabled', 'api_url', 'api_key', 'api_secret',
  'currency', 'language', 'books', 'callback_registered',
];
const VIPER_LANGUAGES = [
  { value: 'tr', label: 'Türkçe' },
  { value: 'en', label: 'English' },
  { value: 'de', label: 'Deutsch' },
  { value: 'ru', label: 'Русский' },
  { value: 'fr', label: 'Français' },
  { value: 'es', label: 'Español' },
  { value: 'pt', label: 'Português' },
  { value: 'ar', label: 'العربية' },
  { value: 'az', label: 'Azərbaycanca' },
];
const EXESTRUCT_ODD_TYPES = [
  { value: 0, label: 'Ondalık (2.15)' },
  { value: 1, label: 'Kesirli (23/20)' },
  { value: 2, label: 'Amerikan (+115)' },
];

// Backend, sağlayıcı panelindeki kaydı geçersiz kılabilecek bir kimlik/adres
// değişikliği `callback_registered` olmadan gelirse onayı güvenlik gereği
// düşürür. Panelde kutu zaten işaretliyken alan değiştirildiğinde kutu dirty
// sayılmıyordu; görünürde onaylı form bu yüzden 400 alıyordu. Kritik alanla
// birlikte ekrandaki mevcut onay da açıkça gönderilir.
export const CALLBACK_CONFIRMATION_RULES = {
  flexrix: {
    key: 'flexrix_callback_registered',
    invalidatedBy: [
      'flexrix_merchant_key', 'flexrix_api_url', 'flexrix_sports_url',
      'flexrix_jwt_algorithm',
    ],
  },
  betby: {
    key: 'syncode_callback_registered',
    invalidatedBy: ['syncode_agent_code', 'syncode_api_url'],
  },
  emsports: {
    key: 'callback_registered',
    invalidatedBy: ['merchant_key', 'sports_url'],
  },
  sportdigi: {
    key: 'callback_registered',
    invalidatedBy: ['merchant_key', 'sports_url'],
  },
  exestruct: {
    key: 'callback_registered',
    invalidatedBy: ['agent_code', 'api_url'],
  },
  viper: {
    key: 'callback_registered',
    invalidatedBy: ['api_url', 'api_key'],
  },
};

// Anahtar değişiminden sonra sağlayıcıda eski kimliğe bağlı kalmış kuponun
// sonucu eski sırla imzalanıp gelir; backend onu süreli olarak kabul eder
// (services/credential_rotation.py). Pencere açıkken yöneticinin bunu
// görmesi gerekir: kapatıldığı sanılan bir kimliğin hâlâ çalıştığını
// hiçbir ekran söylemezse kapı sessiz kalır.
const rotationNote = (config) => {
  if (!config || !config.previous_identity_active) return '';
  const key = String(config.previous_merchant_key || '').trim();
  const stamp = String(config.previous_rotated_at || '').slice(0, 10);
  const parca = [key && `eski merchant ${key}`, stamp && `${stamp} tarihinde değişti`]
    .filter(Boolean)
    .join(' · ');
  return `Geçiş penceresi açık${parca ? ` (${parca})` : ''}: değişimden önce`
    + ' açılmış kuponların sonucu eski kimlikle de kabul edilir.';
};

const csvList = (value) => String(value || '')
  .split(',')
  .map((item) => item.trim())
  .filter(Boolean);

const toggleCsv = (value, code, allowed) => {
  const current = csvList(value);
  const next = current.includes(code)
    ? current.filter((item) => item !== code)
    : [...current, code];
  // Boş liste sağlayıcıya geçersiz istek attırır; son seçenek kaldırılamaz.
  const ordered = allowed.filter((item) => next.includes(item));
  return (ordered.length ? ordered : current).join(',');
};

// Exestruct'ın iki sırrı da maskeyle çizilir; maske gönderilmez (bkz.
// `exestructDirty`).
const hydrateExestruct = (data) => {
  const next = { ...data };
  EXESTRUCT_SECRETS.forEach((key) => {
    next[key] = data[`${key}_configured`] ? MASKED_SECRET : '';
  });
  return next;
};

const hydrateViper = (data) => ({
  ...data,
  api_secret: data.api_secret_configured ? MASKED_SECRET : '',
});

const hydrateSecrets = (data) => {
  const next = { ...data };
  SECRET_KEYS.forEach((key) => {
    next[key] = data[`${key}_configured`] ? MASKED_SECRET : '';
  });
  return next;
};

const DAY_OPTIONS = [
  { value: 1, label: '24 saat' },
  { value: 3, label: '3 gün' },
  { value: 7, label: '7 gün' },
  { value: 14, label: '14 gün' },
  { value: 30, label: '30 gün' },
];

// Kaynak başına bir renk: alanların hangi sağlayıcıya ait olduğu bölümün sol
// kenarından da okunur. Eskiden Sportsbook alanları hiçbir kutuya bağlı
// olmadığı için API-Football'un devamı gibi görünüyordu.
const TONES = {
  betby: '#f59e0b',
  flexrix: '#f43f5e',
  emsports: '#22d3ee',
  sportdigi: '#f97316',
  exestruct: '#e11d48',
  viper: '#84cc16',
  apifootball: '#34d399',
  sportsbook: '#a78bfa',
  oddsapi: '#38bdf8',
  cashout: '#fbbf24',
  neutral: '#94a3b8',
};

// Sayfa bir sağlayıcı listesi + üç araç bölümüdür; tamamı açıkken ekranda
// kaybolunuyordu. Her bölüm katlanır, açık olanlar burada tutulur.
// Kupon listesi bu sayfadan çıkarıldı: kendi bölümü var (Spor → Spor
// Bahisleri). Özel Oranlar'daki "Kuponlar" düğmesi oraya yönlendirir.
const SECTION_IDS = [
  // Sıra ekrandaki sırayla aynıdır: hazır spor kitapları → oran kaynakları →
  // görünüm ve sıralama → bahis kuralları ve araçlar.
  'flexrix', 'emsports', 'sportdigi', 'exestruct', 'viper', 'betby',
  'apifootball', 'sportsbook', 'oddsapi',
  'brand', 'order', 'logos',
  'cashout', 'odds', 'results', 'feed',
];

// Flexrix bağlantı testinin sonucu üç aşamalıdır ve ayrımı yapmak şart:
// abonelik açılmadan sağlayıcının çoğu ucu gövdesiz 401 döndüğü için "anahtar
// yanlış" gibi görünüyor, oysa anahtar doğru olabilir (canlıda ölçüldü,
// 03.09.2026). Metin toast ile satırda aynı olsun diye tek yerden gelir.
const flexrixStatusTitle = (status) => {
  if (!status) return 'BetConstruct bağlantısı kurulamadı';
  if (status.connected) return 'BetConstruct bağlantısı çalışıyor';
  // Kenar katmanı (Cloudflare) reddi sağlayıcının kararı **değildir**: istek
  // uygulamaya hiç ulaşmamıştır. "Abonelik kapalı" diye yazılırsa yönetici
  // sağlayıcıya yazar, oysa yapılacak iş bizdedir (ölçüm 05.09.2026).
  if (status.error_code === 'IP_NOT_ALLOWED') {
    return 'BetConstruct isteği kenar katmanında kesildi';
  }
  if (status.error_code === 'RATE_LIMITED') {
    return 'BetConstruct istek sınırına takıldı';
  }
  if (status.merchant_known) return 'BetConstruct aboneliği aktif değil';
  if (status.reachable) return 'BetConstruct merchant key tanınmadı';
  return 'BetConstruct bağlantısı kurulamadı';
};

// Sayaç süreç belleğindedir ve yeniden başlatmada sıfırlanır; "son 24 saat"
// demek yanlış olurdu. Gerçekte ne kadarlık bir pencereye bakıldığı yazılır,
// yoksa yönetici düşük bir sayıyı "trafiğimiz az" sanır.
const flexrixTrafficWindow = (traffic) => {
  const minutes = Number(traffic?.observed_minutes || 0);
  if (minutes >= 60) {
    const hours = Math.floor(minutes / 60);
    return `${hours} saatte`;
  }
  return `${minutes} dakikada`;
};

const Field = ({ label, children, hint, full = false }) => (
  <label className={`gc-field ${full ? 'is-full' : ''}`}>
    <span className="gc-label">{label}</span>
    {children}
    {hint && <span className="gc-hint">{hint}</span>}
  </label>
);

// Kayıtlı bir sır etiketin yanındaki işaretten okunur: alan boş görünse de
// sunucuda bir değer duruyor demektir ve boş bırakmak onu silmez.
const SecretField = ({ label, value, configured, placeholder, onChange, full = true }) => (
  <Field
    full={full}
    label={(
      <>
        {label}
        {configured && <span className="gc-label-tag is-ok">kayıtlı</span>}
      </>
    )}
  >
    <input
      type="password"
      value={value || ''}
      onFocus={() => { if (value === MASKED_SECRET) onChange(''); }}
      onChange={(event) => onChange(event.target.value)}
      className="gc-input"
      placeholder={configured ? 'Değiştirmek için yeni anahtar girin' : placeholder}
      autoComplete="new-password"
    />
  </Field>
);

const DaySelect = ({ label, value, fallback, onChange }) => (
  <Field label={label}>
    <select
      value={value || fallback}
      onChange={(event) => onChange(Number(event.target.value))}
      className="gc-input"
    >
      {DAY_OPTIONS.map((option) => (
        <option key={option.value} value={option.value}>{option.label}</option>
      ))}
    </select>
  </Field>
);

const MarginField = ({ label, value, fallback, onChange }) => (
  <Field label={label}>
    <input
      type="number"
      min="0"
      max="0.5"
      step="0.01"
      value={value ?? fallback}
      onChange={(event) => onChange(Number(event.target.value))}
      className="gc-input"
    />
  </Field>
);

// Aç/kapa başlık satırındadır: bölümü açmadan da kaynağın durumu değiştirilir.
// Katlama düğmesinin içine konamaz — iç içe düğme tıklamayı ikiye böler.
// Ray düğmenin kendisi değil içindeki <span>'dir; gerekçesi gameControl.css
// içindeki `.gc-switch` kuralının yanında yazılıdır.
const Switch = ({ checked, onChange, label, tone = '#34d399' }) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    aria-label={label}
    title={label}
    onClick={() => onChange(!checked)}
    className="gc-switch"
    style={{ '--gc-tone': tone }}
  >
    <span className="gc-switch-track" />
  </button>
);

const Tag = ({ tone, children }) => (
  <span className="gc-badge is-tone" style={tone ? { '--gc-tone': tone } : undefined}>
    {children}
  </span>
);

// Başlık satırı her zaman görünür — durum, uyarı ve aç/kapa katlanmış hâlde de
// okunur; yalnızca ayar alanları gizlenir.
const Section = ({
  tone = TONES.neutral, icon: Icon, title, subtitle, tags = [], badge,
  control, open, onToggle, alert, children,
}) => (
  <section
    className={`gc-sect ${open ? 'is-open' : ''}`}
    style={{ '--gc-tone': tone }}
  >
    <div className="gc-sect-head">
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        className="gc-sect-toggle"
      >
        <ChevronDown size={15} className="gc-sect-chevron" />
        <span className="gc-sect-icon"><Icon size={15} /></span>
        <span className="gc-sect-text">
          <span className="gc-sect-title">
            {title}
            {tags.map((tag) => <Tag key={tag} tone={tone}>{tag}</Tag>)}
          </span>
          {subtitle && <span className="gc-sect-sub">{subtitle}</span>}
        </span>
      </button>
      {alert && (
        <span className="gc-sect-alert">
          <AlertTriangle size={10} /> Anahtar yok
        </span>
      )}
      {badge}
      {control}
    </div>
    {open && (
      <div className="gc-sect-body">
        {alert && <p className="gc-note is-warn">{alert}</p>}
        {children}
      </div>
    )}
  </section>
);

// ``warn``: ayarlar tamam ve anahtar açık ama sağlayıcı tarafı işi kabul
// etmiyor. Yeşil "Açık" rozeti o durumda yalan söylüyordu — yönetici panelde
// her şeyi doğru görürken oyuncu /spor sayfasında hata alıyordu.
const StatusBadge = ({
  enabled, warn = false, warnLabel = 'Açık · sağlayıcı pasif', intent = null,
}) => {
  if (enabled && warn) {
    return <span className="gc-badge is-warn">{warnLabel}</span>;
  }
  // Anahtar açıkken "Kapalı" yazmak çelişkiliydi: yönetici rayı açık görüyor,
  // rozet kapalı diyordu. Anahtar yöneticinin **isteğidir**, rozet yayında
  // olup olmadığıdır; eksik ayar varsa ikisi ayrı ayrı söylenir.
  if (!enabled && intent) {
    return <span className="gc-badge is-warn">Açık · yayında değil</span>;
  }
  return (
    <span className={`gc-badge ${enabled ? 'is-on' : ''}`}>
      {enabled ? 'Açık' : 'Kapalı'}
    </span>
  );
};

// Oyuncunun tarayıcısında açılan bir adres panelin alan adını ifşa ediyorsa
// yönetici bunu görmelidir: yazma kapısı yalnızca **yeni** değeri durdurur,
// önceden kaydedilmiş bir adres sessizce sızmaya devam ederdi. İfşa edilen
// panel adresi sunucudan gelir (`*_exposes_panel`); istemcide yeniden
// hesaplanmaz — kayıtlı alan adı haritası panele hiç gönderilmez.
const PanelDomainLeak = ({ host }) => (!host ? null : (
  <div className="gc-note is-warn">
    <AlertTriangle size={13} />
    <span>
      Bu adres yönetim panelinin alan adını (<b>{host}</b>) ifşa ediyor: spor
      kitabı çerçevesi oyuncunun tarayıcısında açılır ve adresi sayfanın
      kaynağında görünür. Panelin alan adından <b>bağımsız</b> bir alt alan adı
      kullanın — DNS'te sağlayıcının kenar adresine bir CNAME kaydı açıp
      sağlayıcıdan o alan adını aktive etmesini isteyin, sonra buraya yazın.
    </span>
  </div>
));

// Sunucu taban adresi veremediğinde (kurulum yeni ya da panel ayrı bir alan
// adında ve ana site henüz hiç ziyaret edilmedi) callback adresi üretilemez.
// Panel o zaman yarım bir adres değil, ne yapılacağını gösterir.
const CallbackBaseMissing = () => (
  <div className="gc-note is-warn">
    <AlertTriangle size={13} />
    <span>
      Sitenin adresi çözülemedi, bu yüzden tam adres yazılamıyor. Adres
      <b> https://&lt;sitenizin adresi&gt;/api/…</b> biçimindedir. Kalıcı çözüm:
      <b> Operasyon → Alan Adları</b> ekranından <b>oyun bildirimleri</b> rolüne
      bir alan adı atayın; adres o rolden üretilir ve domain değişince geride
      kalmaz.
    </span>
  </div>
);

// Flexrix'in "yayında mı" kararı iki yerde okunur: ekranı çizen kod ve
// bölüm açılışındaki sessiz yoklama. Hook'lar bileşenin erken return'lerinden
// önce çalışmak zorunda olduğu için koşul saf bir fonksiyona alındı —
// gövdede tekrar yazılsaydı ikisi zamanla ayrışırdı.
export const flexrixConfiguredOf = (settings) => Boolean(
  settings
  && String(settings.flexrix_api_url || '').trim()
  && String(settings.flexrix_merchant_key || '').trim()
  && (settings.flexrix_api_secret_configured
      || String(settings.flexrix_api_secret || '').trim())
  && String(settings.flexrix_sports_url || '').trim(),
);

export const flexrixKeyReadyOf = (settings) => Boolean(
  settings && settings.flexrix_jwt_key_configured,
);

export const flexrixLiveOf = (settings) => Boolean(
  settings
  && settings.flexrix_enabled
  && settings.flexrix_sports_enabled
  && settings.flexrix_callback_registered
  && flexrixConfiguredOf(settings)
  && flexrixKeyReadyOf(settings),
);

// Başlıktaki anahtarın tek başına ne yaptığı: ayrı bir "servis açık" kutusu
// yoktur, anahtar hem sayfayı siteden kaldırır hem yeni bahsi durdurur.
// Süren kuponlar bundan etkilenmez — sağlayıcının kazanç ve iptal bildirimi
// işlenmeye devam eder, aksi hâlde oyuncunun bahsi ortada kalır ve yanar.
const ServiceSwitchNote = () => (
  <p className="gc-note">
    Başlıktaki anahtar kapatıldığında spor sayfası <b>siteden kaldırılır</b> ve
    <b> yeni bahis alınmaz</b>. Süren kuponlar durmaz: kazanç ve iptal
    bildirimleri işlenmeye devam eder, kuponlar bahis geçmişinde görünür ve
    sonucu geldiğinde kapanır.
  </p>
);

const CountBadge = ({ children }) => (
  <span className="gc-badge is-count">{children}</span>
);

const mergeCatalogOrder = (available, preferred) => [
  ...(preferred || []).filter((code) => available.includes(code)),
  ...available.filter((code) => !(preferred || []).includes(code)),
];

// Sıralama alanları dizidir; referans değil içerik karşılaştırılmalı.
const sameValue = (left, right) => (
  JSON.stringify(left ?? null) === JSON.stringify(right ?? null)
);

export const withCallbackConfirmation = (
  payload,
  changedKeys,
  current,
  rules,
) => {
  const next = { ...payload };
  rules.forEach((rule) => {
    if (changedKeys.some((key) => rule.invalidatedBy.includes(key))) {
      next[rule.key] = Boolean(current?.[rule.key]);
    }
  });
  return next;
};

// Bir kart kaydedildiğinde yalnızca o karta ait alanlar sunucu yanıtıyla
// tazelenir. Diğer kartlarda henüz kaydedilmemiş değerler formda kalmalıdır;
// bütün yanıtı state'e basmak bir kartın Kaydet'iyle diğer kartın taslağını
// sessizce siliyordu.
export const mergeSavedFields = (current, saved, keys) => {
  if (!keys) return saved;
  const next = { ...(current || {}) };
  keys.forEach((key) => {
    if (Object.prototype.hasOwnProperty.call(saved, key)) next[key] = saved[key];
  });
  return next;
};

// Yeni panel tasarımında bu bileşen iki ayrı sayfanın parçasıdır:
// `view` bir spor kitabının kimliğiyse (BOOK_IDS) yalnızca o kitabın bölümü
// açık çizilir (Spor → Spor Sağlayıcıları), `odds` ise kitaplar dışındaki her
// şeyi çizer (Spor → Oran & Cashout). Değer verilmezse klasik görünümün tek
// sayfası olduğu gibi kalır.
export const BOOK_IDS = ['flexrix', 'emsports', 'sportdigi', 'exestruct', 'viper', 'betby'];

const SportsControlPanel = ({ view } = {}) => {
  const bookView = BOOK_IDS.includes(view) ? view : null;
  const showBook = (id) => !view || bookView === id;
  const showRest = !bookView;
  const [settings, setSettings] = useState(null);
  // Sunucudaki son hâl: istek gövdesine yalnızca gerçekten değişen alanlar
  // konur. Backend ayarları alan bazında yetkilendirdiği için (spor besleme
  // anahtarları finance.write, `sports_enabled` content.write), dokunulmamış
  // alanı da göndermek yöneticiye gereksiz 403 döndürürdü.
  const [initial, setInitial] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  // Callback adresi kopyalandı geri bildirimi (Betby kartı).
  const [betbyCopied, setBetbyCopied] = useState(false);
  // Flexrix kartı: kopyalanan adres (hangisi olduğu da tutulur — iki
  // ayrı adres vardır), anahtar kaydetme ve bağlantı testi.
  const [flexrixCopied, setFlexrixCopied] = useState('');
  const [flexrixBusy, setFlexrixBusy] = useState('');
  const [flexrixSsoKeyDraft, setFlexrixSsoKeyDraft] = useState('');
  const [flexrixStatus, setFlexrixStatus] = useState(null);
  // Kayıtlı özel anahtardan türetilen açık anahtar (sunucudan gelir) ve
  // hub'dakiyle karşılaştırma sonucu.
  const [flexrixPublicKey, setFlexrixPublicKey] = useState('');
  const [flexrixPublicKeyDraft, setFlexrixPublicKeyDraft] = useState('');
  const [flexrixKeyMatch, setFlexrixKeyMatch] = useState('');
  // EveryMatrix kartı: gövde ayrı bir uçtan gelir ve ayrı kaydedilir
  // (`settings` kaydında durmuyor), bu yüzden kendi "kaydedilmemiş değişiklik"
  // ölçüsü ve kendi Kaydet düğmesi vardır.
  const [emsports, setEmsports] = useState(null);
  const [emsportsInitial, setEmsportsInitial] = useState(null);
  const [emsportsBusy, setEmsportsBusy] = useState('');
  const [emsportsStatus, setEmsportsStatus] = useState(null);
  const [emsportsCopied, setEmsportsCopied] = useState(false);
  // Digitain kartı: EveryMatrix ile aynı sözleşmeyi paylaşır ama **ayrı bir
  // sağlayıcıdır** — kendi sırrı, kendi `provider_configs` satırı ve kendi
  // uçları vardır; ortak bir state'e bağlamak iki kurulumu birbirine karıştırırdı.
  const [sportdigi, setSportdigi] = useState(null);
  // Flexrix'in kimliği `settings`'tedir ama anahtar değişimindeki eski
  // kimlik `provider_configs` satırında durur; ayrı uçtan okunur.
  const [flexrixRotation, setFlexrixRotation] = useState(null);
  // Ödenemeyen kazançlar ve maç durumu bildirimi bir ayar değil,
  // sağlayıcının o anki durumudur: istendiğinde okunur, kaydedilmez.
  const [flexrixDead, setFlexrixDead] = useState(null);
  const [flexrixMatchPush, setFlexrixMatchPush] = useState(null);
  const [sportdigiInitial, setSportdigiInitial] = useState(null);
  const [sportdigiBusy, setSportdigiBusy] = useState('');
  const [sportdigiStatus, setSportdigiStatus] = useState(null);
  const [sportdigiCopied, setSportdigiCopied] = useState(false);
  // Öne çıkan maç cevabının biçimi belgelenmemiştir; şerit boş kaldığında
  // sağlayıcının gerçekte ne gönderdiği buradan görülür.
  const [sportdigiMatches, setSportdigiMatches] = useState(null);
  // Exestruct kartı: Flexrix ailesinden değildir, kendi satırı ve uçları var.
  const [exestruct, setExestruct] = useState(null);
  const [exestructInitial, setExestructInitial] = useState(null);
  const [exestructBusy, setExestructBusy] = useState('');
  const [exestructStatus, setExestructStatus] = useState(null);
  const [exestructCopied, setExestructCopied] = useState(false);
  // Viper kartı: kendi satırı, kendi uçları; spor kitabı listesi katalogdan
  // istendiğinde okunur (sağlayıcıya ulaşamayan kurulumda sayfa beklemesin).
  const [viper, setViper] = useState(null);
  const [viperInitial, setViperInitial] = useState(null);
  const [viperBusy, setViperBusy] = useState('');
  const [viperStatus, setViperStatus] = useState(null);
  const [viperBooks, setViperBooks] = useState(null);
  const [viperCopied, setViperCopied] = useState(false);
  // Oyuncunun gördüğü sağlayıcı geçişinin markası (logo + görünen ad).
  const [branding, setBranding] = useState([]);
  const [brandBusy, setBrandBusy] = useState('');
  const [brandUploading, setBrandUploading] = useState('');
  const [results, setResults] = useState([]);
  const [scores, setScores] = useState({});
  const [resultBusy, setResultBusy] = useState('');
  const [feedBusy, setFeedBusy] = useState(false);
  const [feedStatus, setFeedStatus] = useState(null);
  const [competitions, setCompetitions] = useState([]);
  const [catalogSport, setCatalogSport] = useState('SOCCER');
  // Lig sıralaması kaynak bazında ayrı ayarda tutulduğu için liste tek seferde
  // tek kaynağı gösterir; aksi hâlde taşıma hangi listeyi değiştireceğini
  // bilemezdi.
  const [catalogFeed, setCatalogFeed] = useState('sportsbook');
  const [quota, setQuota] = useState(null);
  // Takım logoları vitrin isteğinde çözülmez; kuyruk sayaçları ve eşitleme
  // düğmesi burada durur.
  const [logoStatus, setLogoStatus] = useState(null);
  const [logoBusy, setLogoBusy] = useState('');
  // Özel oran listesi kendi bileşeninde durur; buraya yalnızca başlıktaki
  // sayaç için adet bildirilir.
  const [overrideCount, setOverrideCount] = useState(0);
  const [openSections, setOpenSections] = useState(() => (bookView ? { [bookView]: true } : {}));
  const { toast } = useToast();
  const navigate = useNavigate();

  const toggleSection = useCallback((id) => {
    setOpenSections((current) => ({ ...current, [id]: !current[id] }));
  }, []);

  // "Kuponlar" düğmesi Spor Bahisleri sayfasını o tanımın imzasıyla açar.
  // Süzgeç adres çubuğunda taşınır: bileşen state'inde tutulsaydı sayfa
  // yenilendiğinde ya da bağlantı paylaşıldığında liste bütün kuponlara
  // dönerdi ve yönetici baktığı tanımı kaybederdi.
  const inspectOverride = useCallback((target) => {
    const params = new URLSearchParams({ override: target.signature });
    if (target.label) params.set('override_label', target.label);
    navigate(`/admin/sports-bets?${params.toString()}`);
  }, [navigate]);
  const setAllSections = (value) => {
    setOpenSections(Object.fromEntries(SECTION_IDS.map((id) => [id, value])));
  };

  const loadQuota = useCallback(async () => {
    try {
      const { data } = await client.get('/admin/sports/oddsapi-quota');
      setQuota(data.available ? data : null);
    } catch {
      setQuota(null);
    }
  }, []);

  const loadResults = useCallback(async () => {
    try {
      const { data } = await client.get('/admin/sports/pending-results');
      setResults(data.items || []);
    } catch {
      setResults([]);
    }
  }, []);

  // `provider_configs` satırında yaşayan sağlayıcılar (EveryMatrix, Digitain)
  // ve marka listesi tek turda okunur. Hata sağlayıcı başına **ayrı** yutulur:
  // biri hiç kurulmamış olabilir ve o yüzden diğerinin kartını da boş
  // göstermek yanlış olurdu.
  const loadHostedSportsbooks = useCallback(async () => {
    const [em, digi, brand, rotation, exe, vip] = await Promise.all([
      client.get('/admin/emsports/config').catch(() => null),
      client.get('/admin/sportdigi/config').catch(() => null),
      client.get('/admin/emsports/branding')
        .catch(() => ({ data: { items: [] } })),
      client.get('/admin/flexrix/rotation').catch(() => null),
      client.get('/admin/exestruct/config').catch(() => null),
      client.get('/admin/viper/config').catch(() => null),
    ]);
    setFlexrixRotation(rotation ? rotation.data : null);
    const hydrate = (response) => (response ? {
      ...response.data,
      api_secret: response.data.api_secret_configured ? MASKED_SECRET : '',
    } : null);
    const emBody = hydrate(em);
    setEmsports(emBody);
    setEmsportsInitial(emBody);
    const digiBody = hydrate(digi);
    setSportdigi(digiBody);
    setSportdigiInitial(digiBody);
    const exeBody = exe ? hydrateExestruct(exe.data) : null;
    setExestruct(exeBody);
    setExestructInitial(exeBody);
    const viperBody = vip ? hydrateViper(vip.data) : null;
    setViper(viperBody);
    setViperInitial(viperBody);
    setBranding(brand.data.items || []);
  }, []);

  const loadLogoStatus = useCallback(async () => {
    try {
      const { data } = await client.get('/admin/sports/team-logos');
      setLogoStatus(data);
    } catch {
      setLogoStatus(null);
    }
  }, []);

  const syncLogos = useCallback(async () => {
    setLogoBusy('sync');
    try {
      const { data } = await client.post('/admin/sports/team-logos/sync');
      setLogoStatus(data);
      toast({
        title: 'Logolar eşitlendi',
        description: data.stopped
          ? `${data.resolved} logo bulundu, tur erken durdu (${data.stopped}). Kalan ${data.pending} ad için tekrar deneyin.`
          : `${data.checked} ad denendi · ${data.resolved} logo bulundu · ${data.unmatched} eşleşmedi. Kuyrukta ${data.pending} ad kaldı.`,
      });
    } catch (error) {
      toast({
        title: 'Logolar eşitlenemedi',
        description: error.response?.data?.detail || 'İstek başarısız',
      });
    } finally {
      setLogoBusy('');
    }
  }, [toast]);

  const retryMissingLogos = useCallback(async () => {
    setLogoBusy('retry');
    try {
      const { data } = await client.post('/admin/sports/team-logos/retry');
      await loadLogoStatus();
      toast({
        title: 'Kuyruğa alındı',
        description: `${data.queued} ad yeniden aranacak.`,
      });
    } catch (error) {
      toast({
        title: 'İşlem başarısız',
        description: error.response?.data?.detail || 'İstek başarısız',
      });
    } finally {
      setLogoBusy('');
    }
  }, [toast, loadLogoStatus]);

  useEffect(() => {
    let active = true;
    Promise.all([
      client.get('/admin/settings'),
      client.get('/admin/sports/pending-results').catch(() => ({ data: { items: [] } })),
      client.get('/sports/book/competitions').catch(() => ({ data: { competitions: [] } })),
    ])
      .then(([settingsResponse, resultsResponse, competitionsResponse]) => {
        if (!active) return;
        const hydrated = hydrateSecrets(settingsResponse.data);
        setSettings(hydrated);
        setInitial(hydrated);
        const pending = resultsResponse.data.items || [];
        setResults(pending);
        const rows = competitionsResponse.data.competitions || [];
        setCompetitions(rows);
        setCatalogSport(competitionsResponse.data.default_sport || 'SOCCER');
        setCatalogFeed(rows[0]?.feed || 'sportsbook');
        // Açılışta yalnızca ilgi isteyen bölümler açılır: anahtarı eksik kaynak
        // ve bekleyen sonuç. Kalanı katlı kalır.
        const needsKey = (enabledKey, secretKey) => (
          !!hydrated[enabledKey] && !hydrated[`${secretKey}_configured`]
        );
        setOpenSections({
          apifootball: needsKey('apifootball_enabled', 'apifootball_api_key'),
          sportsbook: needsKey('sportsbook_api_enabled', 'sportsbook_api_key'),
          oddsapi: needsKey('oddsapi_enabled', 'oddsapi_api_key'),
          results: pending.length > 0,
        });
        if (hydrated.oddsapi_enabled) loadQuota();
        loadLogoStatus();
        loadHostedSportsbooks();
      })
      .catch((error) => {
        if (active) {
          toast({
            title: 'Spor ayarları alınamadı',
            description: error.response?.data?.detail || 'İstek başarısız',
          });
        }
      })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [toast, loadQuota, loadLogoStatus, loadHostedSportsbooks]);

  const update = (key, value) => {
    setSettings((current) => ({ ...current, [key]: value }));
  };

  const updateMany = (patch) => {
    setSettings((current) => ({ ...current, ...patch }));
  };

  // Kaydet gövdesi ve "kaydedilmemiş değişiklik" rozeti aynı listeden türer.
  const dirtyKeys = useMemo(() => {
    if (!settings || !initial) return [];
    return SETTING_KEYS.filter((key) => {
      if (!Object.prototype.hasOwnProperty.call(settings, key)) return false;
      if (sameValue(settings[key], initial[key])) return false;
      if (SECRET_KEYS.includes(key) && (!settings[key] || settings[key] === MASKED_SECRET)) {
        return false;
      }
      return true;
    });
  }, [settings, initial]);

  const moveCatalogItem = (
    key,
    code,
    direction,
    availableCodes,
    groupCodes = availableCodes,
  ) => {
    const ordered = mergeCatalogOrder(availableCodes, settings[key]);
    const visible = ordered.filter((item) => groupCodes.includes(item));
    const currentIndex = visible.indexOf(code);
    const targetIndex = currentIndex + direction;
    if (currentIndex < 0 || targetIndex < 0 || targetIndex >= visible.length) return;
    const currentPosition = ordered.indexOf(code);
    const targetPosition = ordered.indexOf(visible[targetIndex]);
    [ordered[currentPosition], ordered[targetPosition]] = [
      ordered[targetPosition], ordered[currentPosition],
    ];
    update(key, ordered);
  };

  // `keys` verilirse yalnızca o bölümün alanları yazılır; verilmezse
  // değişmiş her alan gider (alttaki şerit).
  const save = async (keys = null) => {
    const target = keys ? dirtyKeys.filter((key) => keys.includes(key)) : dirtyKeys;
    if (!target.length) {
      toast({ title: 'Kaydedilecek değişiklik yok' });
      return;
    }
    const changed = {};
    target.forEach((key) => { changed[key] = settings[key]; });
    const payload = withCallbackConfirmation(
      changed,
      target,
      settings,
      [CALLBACK_CONFIRMATION_RULES.flexrix, CALLBACK_CONFIRMATION_RULES.betby],
    );
    setSaving(true);
    try {
      const { data } = await client.patch('/admin/settings', payload);
      const hydrated = hydrateSecrets(data);
      // Sırların maskesi de aynı kartın kaydedilmiş sonucudur; eşlik eden
      // `_configured` bayrağı alınmazsa yeni secret kaydedildiği hâlde alan
      // boş görünürdü.
      const responseKeys = keys ? [
        ...keys,
        ...SECRET_KEYS
          .filter((key) => keys.includes(key))
          .map((key) => `${key}_configured`),
      ] : null;
      setSettings((current) => mergeSavedFields(current, hydrated, responseKeys));
      setInitial((current) => mergeSavedFields(current, hydrated, responseKeys));
      notifySettingsUpdated();
      // Flexrix anahtarı değiştiyse geçiş penceresi az önce açıldı.
      if (target.some((key) => key === 'flexrix_merchant_key'
          || key === 'flexrix_api_secret')) {
        client.get('/admin/flexrix/rotation')
          .then(({ data: rotation }) => setFlexrixRotation(rotation))
          .catch(() => {});
      }
      toast({ title: 'Spor ve cashout ayarları kaydedildi' });
    } catch (error) {
      toast({
        title: 'Spor ayarları kaydedilemedi',
        description: error.response?.data?.detail || 'İşlem başarısız',
      });
    } finally {
      setSaving(false);
    }
  };

  const testFeed = async () => {
    setFeedBusy(true);
    try {
      const { data } = await client.get('/sports/book/competitions');
      const competitionRows = data.available ? (data.competitions || []) : [];
      if (!competitionRows.length) throw new Error('Yarışma listesi boş döndü');
      const days = Math.max(
        settings.sportsbook_api_enabled
          ? Number(settings.sportsbook_upcoming_days) || 14 : 0,
        settings.oddsapi_enabled
          ? Number(settings.oddsapi_upcoming_days) || 7 : 0,
        1,
      );
      const eventResponse = await client.get('/sports/book/events', {
        params: { days },
      });
      const nextStatus = {
        competitions: competitionRows.length,
        competition: 'Tüm ligler',
        feed: (data.feeds || [])
          .map((feed) => FEED_LABELS[feed] || feed).join(' + '),
        events: eventResponse.data.count || 0,
      };
      setFeedStatus(nextStatus);
      // Kalan kredi yalnızca sağlayıcıya gidilen isteğin yanıt başlığından
      // okunur; testten hemen sonra tazelenmesi bu yüzden anlamlıdır.
      if (settings.oddsapi_enabled) await loadQuota();
      toast({
        title: 'Spor beslemesi çalışıyor',
        description: `${nextStatus.competitions} lig · ${nextStatus.competition}: ${nextStatus.events} maç`,
      });
    } catch (error) {
      setFeedStatus(null);
      toast({
        title: 'Spor beslemesi test edilemedi',
        description: error.response?.data?.detail || error.message || 'İstek başarısız',
      });
    } finally {
      setFeedBusy(false);
    }
  };

  const submitResult = async (eventKey, asVoid = false) => {
    const currentScores = scores[eventKey] || {};
    if (!asVoid && (
      currentScores.home === undefined || currentScores.away === undefined
      || currentScores.home === '' || currentScores.away === ''
    )) {
      toast({ title: 'Skor eksik', description: 'Ev sahibi ve deplasman skorunu girin.' });
      return;
    }
    setResultBusy(eventKey);
    try {
      const { data } = await client.post('/admin/sports/results', {
        event_key: eventKey,
        home_score: asVoid ? null : Number(currentScores.home),
        away_score: asVoid ? null : Number(currentScores.away),
        void: asVoid,
      });
      toast({
        title: asVoid ? 'Olay iptal edildi' : 'Sonuç kaydedildi',
        description: `${data.settle?.settled ?? 0} kupon sonuçlandırıldı`,
      });
      await loadResults();
    } catch (error) {
      toast({
        title: 'Sonuç kaydedilemedi',
        description: error.response?.data?.detail || 'Bir hata oluştu',
      });
    } finally {
      setResultBusy('');
    }
  };

  // ---- EveryMatrix bölümünün kendi işleri --------------------------------
  const updateEmsports = (key, value) => {
    setEmsports((current) => ({ ...(current || {}), [key]: value }));
  };

  const updateEmsportsMany = (patch) => {
    setEmsports((current) => ({ ...(current || {}), ...patch }));
  };

  // Gövdeye yalnızca gerçekten değişen alanlar konur: maskesi bozulmamış
  // secret gönderilirse kayıtlı anahtar maskeyle ezilirdi.
  const emsportsDirty = useMemo(() => {
    if (!emsports || !emsportsInitial) return [];
    return EMSPORTS_KEYS.filter((key) => {
      if (sameValue(emsports[key], emsportsInitial[key])) return false;
      if (key === 'api_secret'
          && (!emsports[key] || emsports[key] === MASKED_SECRET)) {
        return false;
      }
      return true;
    });
  }, [emsports, emsportsInitial]);

  const saveEmsports = async () => {
    if (!emsportsDirty.length) {
      toast({ title: 'Kaydedilecek değişiklik yok' });
      return;
    }
    const changed = {};
    emsportsDirty.forEach((key) => { changed[key] = emsports[key]; });
    const payload = withCallbackConfirmation(
      changed,
      emsportsDirty,
      emsports,
      [CALLBACK_CONFIRMATION_RULES.emsports],
    );
    setEmsportsBusy('save');
    try {
      const { data } = await client.patch('/admin/emsports/config', payload);
      const hydrated = {
        ...data,
        api_secret: data.api_secret_configured ? MASKED_SECRET : '',
      };
      setEmsports(hydrated);
      setEmsportsInitial(hydrated);
      // Spor kitabı listesi `/settings` yanıtındadır: kaydettikten sonra
      // tazelenmezse oyuncu sayfası eski sağlayıcı kümesini çizmeye devam
      // ederdi.
      notifySettingsUpdated();
      toast({ title: 'EveryMatrix ayarları kaydedildi' });
    } catch (error) {
      toast({
        title: 'EveryMatrix ayarları kaydedilemedi',
        description: error.response?.data?.detail || 'İşlem başarısız',
      });
    } finally {
      setEmsportsBusy('');
    }
  };

  const testEmsports = async () => {
    setEmsportsBusy('test');
    try {
      const { data } = await client.post('/admin/emsports/test');
      setEmsportsStatus(data);
      toast({
        title: data.connected
          ? 'EveryMatrix bağlantısı çalışıyor'
          : 'EveryMatrix bağlantısı kurulamadı',
        description: data.connected
          ? 'Merchant key ve imza kabul edildi.'
          : data.error || 'Sağlayıcı isteği reddetti.',
      });
    } catch (error) {
      setEmsportsStatus(null);
      toast({
        title: 'EveryMatrix test edilemedi',
        description: error.response?.data?.detail || 'İstek başarısız',
      });
    } finally {
      setEmsportsBusy('');
    }
  };

  // ---- Exestruct bölümünün kendi işleri ---------------------------------
  const updateExestruct = (key, value) => {
    setExestruct((current) => ({ ...(current || {}), [key]: value }));
  };

  // Maskesi bozulmamış sır gönderilmez: kayıtlı anahtar maskeyle ezilirdi.
  const exestructDirty = useMemo(() => {
    if (!exestruct || !exestructInitial) return [];
    return EXESTRUCT_KEYS.filter((key) => {
      if (sameValue(exestruct[key], exestructInitial[key])) return false;
      if (EXESTRUCT_SECRETS.includes(key)
          && (!exestruct[key] || exestruct[key] === MASKED_SECRET)) {
        return false;
      }
      return true;
    });
  }, [exestruct, exestructInitial]);

  const saveExestruct = async () => {
    if (!exestructDirty.length) {
      toast({ title: 'Kaydedilecek değişiklik yok' });
      return;
    }
    const changed = {};
    exestructDirty.forEach((key) => { changed[key] = exestruct[key]; });
    const payload = withCallbackConfirmation(
      changed,
      exestructDirty,
      exestruct,
      [CALLBACK_CONFIRMATION_RULES.exestruct],
    );
    setExestructBusy('save');
    try {
      const { data } = await client.patch('/admin/exestruct/config', payload);
      const hydrated = hydrateExestruct(data);
      setExestruct(hydrated);
      setExestructInitial(hydrated);
      notifySettingsUpdated();
      toast({ title: 'Exestruct ayarları kaydedildi' });
    } catch (error) {
      toast({
        title: 'Exestruct ayarları kaydedilemedi',
        description: error.response?.data?.detail || 'İşlem başarısız',
      });
    } finally {
      setExestructBusy('');
    }
  };

  const testExestruct = async () => {
    setExestructBusy('test');
    try {
      const { data } = await client.post('/admin/exestruct/test');
      setExestructStatus(data);
      toast({
        title: data.connected
          ? 'Exestruct bağlantısı çalışıyor'
          : 'Exestruct bağlantısı kurulamadı',
        description: data.connected ? undefined : data.error,
      });
    } catch (error) {
      setExestructStatus(null);
      toast({
        title: 'Exestruct test edilemedi',
        description: error.response?.data?.detail || 'İşlem başarısız',
      });
    } finally {
      setExestructBusy('');
    }
  };

  // ---- Viper bölümünün kendi işleri -------------------------------------
  const updateViper = (key, value) => {
    setViper((current) => ({ ...(current || {}), [key]: value }));
  };

  // Maskesi bozulmamış sır gönderilmez: kayıtlı secret maskeyle ezilirdi.
  const viperDirty = useMemo(() => {
    if (!viper || !viperInitial) return [];
    return VIPER_KEYS.filter((key) => {
      if (sameValue(viper[key], viperInitial[key])) return false;
      if (key === 'api_secret'
          && (!viper[key] || viper[key] === MASKED_SECRET)) {
        return false;
      }
      return true;
    });
  }, [viper, viperInitial]);

  const saveViper = async () => {
    if (!viperDirty.length) {
      toast({ title: 'Kaydedilecek değişiklik yok' });
      return;
    }
    const changed = {};
    viperDirty.forEach((key) => { changed[key] = viper[key]; });
    const payload = withCallbackConfirmation(
      changed,
      viperDirty,
      viper,
      [CALLBACK_CONFIRMATION_RULES.viper],
    );
    setViperBusy('save');
    try {
      const { data } = await client.patch('/admin/viper/config', payload);
      const hydrated = hydrateViper(data);
      setViper(hydrated);
      setViperInitial(hydrated);
      notifySettingsUpdated();
      toast({ title: 'Viper ayarları kaydedildi' });
    } catch (error) {
      toast({
        title: 'Viper ayarları kaydedilemedi',
        description: error.response?.data?.detail || 'İşlem başarısız',
      });
    } finally {
      setViperBusy('');
    }
  };

  const testViper = async () => {
    setViperBusy('test');
    try {
      const { data } = await client.post('/admin/viper/test');
      setViperStatus(data);
      toast({
        title: data.connected
          ? 'Viper bağlantısı çalışıyor'
          : 'Viper bağlantısı kurulamadı',
        description: data.connected ? undefined : data.error,
      });
    } catch (error) {
      setViperStatus(null);
      toast({
        title: 'Viper test edilemedi',
        description: error.response?.data?.detail || 'İşlem başarısız',
      });
    } finally {
      setViperBusy('');
    }
  };

  // Liste kayıtlı kimlikle sorulur; kaydedilmemiş anahtar burada geçerli
  // değildir.
  const loadViperBooks = async () => {
    setViperBusy('books');
    try {
      const { data } = await client.get('/admin/viper/sportsbooks');
      setViperBooks(data.items || []);
      if (!(data.items || []).length) {
        toast({ title: 'Viper kataloğunda spor kitabı yok' });
      }
    } catch (error) {
      setViperBooks(null);
      toast({
        title: 'Spor kitapları okunamadı',
        description: error.response?.data?.detail || 'İşlem başarısız',
      });
    } finally {
      setViperBusy('');
    }
  };

  // Birden çok kitap birlikte yayınlanabilir (BetBy + BetConstruct); her
  // biri oyuncunun şeridinde kendi sekmesidir. Sıra seçim sırasıdır.
  const toggleViperBook = (item) => {
    setViper((current) => {
      const books = Array.isArray(current?.books) ? current.books : [];
      const selected = books.some((book) => book.game_id === item.id);
      return {
        ...(current || {}),
        books: selected
          ? books.filter((book) => book.game_id !== item.id)
          : [...books, {
            game_id: item.id,
            name: item.name,
            provider_code: item.provider_code || '',
          }],
      };
    });
  };

  // ---- Digitain bölümünün kendi işleri ----------------------------------
  const updateSportdigi = (key, value) => {
    setSportdigi((current) => ({ ...(current || {}), [key]: value }));
  };

  const updateSportdigiMany = (patch) => {
    setSportdigi((current) => ({ ...(current || {}), ...patch }));
  };

  // Gövdeye yalnızca gerçekten değişen alanlar konur: maskesi bozulmamış
  // secret gönderilirse kayıtlı anahtar maskeyle ezilirdi.
  const sportdigiDirty = useMemo(() => {
    if (!sportdigi || !sportdigiInitial) return [];
    return SPORTDIGI_KEYS.filter((key) => {
      if (sameValue(sportdigi[key], sportdigiInitial[key])) return false;
      if (key === 'api_secret'
          && (!sportdigi[key] || sportdigi[key] === MASKED_SECRET)) {
        return false;
      }
      return true;
    });
  }, [sportdigi, sportdigiInitial]);

  const saveSportdigi = async () => {
    if (!sportdigiDirty.length) {
      toast({ title: 'Kaydedilecek değişiklik yok' });
      return;
    }
    const changed = {};
    sportdigiDirty.forEach((key) => { changed[key] = sportdigi[key]; });
    const payload = withCallbackConfirmation(
      changed,
      sportdigiDirty,
      sportdigi,
      [CALLBACK_CONFIRMATION_RULES.sportdigi],
    );
    setSportdigiBusy('save');
    try {
      const { data } = await client.patch('/admin/sportdigi/config', payload);
      const hydrated = {
        ...data,
        api_secret: data.api_secret_configured ? MASKED_SECRET : '',
      };
      setSportdigi(hydrated);
      setSportdigiInitial(hydrated);
      // Spor kitabı listesi `/settings` yanıtındadır: kaydettikten sonra
      // tazelenmezse oyuncu sayfası eski sağlayıcı kümesini çizmeye devam
      // ederdi.
      notifySettingsUpdated();
      toast({ title: 'Digitain ayarları kaydedildi' });
    } catch (error) {
      toast({
        title: 'Digitain ayarları kaydedilemedi',
        description: error.response?.data?.detail || 'İşlem başarısız',
      });
    } finally {
      setSportdigiBusy('');
    }
  };

  // Tema sağlayıcıda durur (`sportdigiTheme`): SPA onun kaydından boyanır,
  // yani Kaydet ile gitmez. Yerel kayıt ancak sağlayıcı kabul ettiğinde
  // güncellenir — aksi hâlde panel uygulanmamış bir temayı uygulanmış gibi
  // gösterirdi (Flexrix'teki kuralın aynısı).
  const applySportdigiTheme = async () => {
    setSportdigiBusy('theme');
    try {
      const { data } = await client.patch('/admin/sportdigi/theme', {
        theme: sportdigi?.theme || 'flexrix-red',
      });
      // Yalnızca tema yazılır: cevabın tamamı basılsaydı kartta henüz
      // kaydedilmemiş alanlar (merchant key, adres…) sessizce geri alınırdı.
      setSportdigi((current) => ({ ...(current || {}), theme: data.theme }));
      setSportdigiInitial(
        (current) => ({ ...(current || {}), theme: data.theme }));
      toast({ title: 'Tema uygulandı', description: data.theme });
    } catch (error) {
      toast({
        title: 'Tema uygulanamadı',
        description: error.response?.data?.detail || 'İşlem başarısız',
      });
    } finally {
      setSportdigiBusy('');
    }
  };

  const loadSportdigiMatches = async () => {
    setSportdigiBusy('matches');
    try {
      const { data } = await client.get('/admin/sportdigi/top-matches/raw');
      setSportdigiMatches(data);
      const count = Array.isArray(data?.parsed) ? data.parsed.length : 0;
      toast({
        title: count
          ? `${count} maç çözümlendi`
          : 'Cevapta tanınan maç yok',
        description: count
          ? 'Ana sayfa şeridi bu maçları gösterir.'
          : 'Ham cevabı aşağıda inceleyin; şerit bu durumda çizilmez.',
      });
    } catch (error) {
      setSportdigiMatches(null);
      toast({
        title: 'Öne çıkan maçlar alınamadı',
        description: error.response?.data?.detail || 'İstek başarısız',
      });
    } finally {
      setSportdigiBusy('');
    }
  };

  const testSportdigi = async () => {
    setSportdigiBusy('test');
    try {
      const { data } = await client.post('/admin/sportdigi/test');
      setSportdigiStatus(data);
      toast({
        title: data.connected
          ? 'Digitain bağlantısı çalışıyor'
          : 'Digitain bağlantısı kurulamadı',
        description: data.connected
          ? 'Merchant key ve imza kabul edildi.'
          : data.error || 'Sağlayıcı isteği reddetti.',
      });
    } catch (error) {
      setSportdigiStatus(null);
      toast({
        title: 'Digitain test edilemedi',
        description: error.response?.data?.detail || 'İstek başarısız',
      });
    } finally {
      setSportdigiBusy('');
    }
  };

  const updateBrand = (id, key, value) => {
    setBranding((rows) => rows.map((row) => (
      row.id === id ? { ...row, [key]: value } : row
    )));
  };

  // ``overrides`` yükleme yolundan gelir: ``setBranding`` eşzamansızdır ve
  // hemen ardından okunan satır eski adresi taşırdı — yüklenen logo panelde
  // görünüp kaydedilmemiş olurdu.
  const saveBrand = async (row, overrides = {}) => {
    const next = { ...row, ...overrides };
    setBrandBusy(row.id);
    try {
      await client.patch('/admin/emsports/branding', {
        provider: next.id,
        label: next.label || '',
        logo_url: next.logo_url || '',
      });
      notifySettingsUpdated();
      toast({ title: `${row.default_label} markası kaydedildi` });
    } catch (error) {
      toast({
        title: 'Marka kaydedilemedi',
        description: error.response?.data?.detail || 'İşlem başarısız',
      });
    } finally {
      setBrandBusy('');
    }
  };

  // Yüklenen logo **hemen kaydedilir**: alanı doldurup Kaydet'i beklemek
  // "logoyu yükledim ama sitede çıkmıyor" şikâyetinin en kısa yoludur.
  const uploadBrandLogo = async (row, event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    if (uploadTooLarge(file)) {
      toast({
        title: 'Logo yüklenemedi',
        description: `Dosya en fazla ${UPLOAD_MAX_LABEL} olabilir.`,
      });
      return;
    }
    setBrandUploading(row.id);
    try {
      const body = new FormData();
      body.append('file', file);
      const { data } = await client.post('/admin/uploads/images', body);
      updateBrand(row.id, 'logo_url', data.url);
      await saveBrand(row, { logo_url: data.url });
    } catch (error) {
      toast({
        title: 'Logo yüklenemedi',
        description: error.response?.data?.detail || 'Dosya yükleme başarısız.',
      });
    } finally {
      setBrandUploading('');
    }
  };

  // ---- Flexrix bölümünün kendi hook'ları --------------------------------
  // Buradalar çünkü aşağıda iki erken return var (yükleniyor / yüklenemedi)
  // ve hook'lar her render'da aynı sırayla çalışmak zorunda. Koşullar
  // ``settings`` null olabileceği için saf yardımcılardan okunur.

  // Açık anahtar bir sır değildir: sağlayıcının jetonu doğruladığı değerdir
  // ve hub panelinde durur. Panelde göstermenin tek sebebi karşılaştırma —
  // yanlış çift yapıştırıldığında oyuncu spor kitabına giremez ve bunu
  // anlamanın başka yolu yok (sağlayıcı sebebi ayrıştırmıyor).
  const loadFlexrixPublicKey = useCallback(async () => {
    try {
      const { data } = await client.get('/admin/flexrix/sso-key');
      setFlexrixPublicKey(data.public_key || '');
    } catch {
      setFlexrixPublicKey('');
    }
  }, []);

  // Flexrix bölümü ilk açıldığında bağlantı bir kez sessizce yoklanır.
  // Ayarlar tamam olduğu hâlde sağlayıcı tarafı pasifse panel yeşil "Açık"
  // gösteriyor, arıza ancak oyuncu /spor sayfasında hata alınca duyuluyordu
  // (canlıda ölçüldü, 04.09.2026: merchant tanınıyor, imza kabul ediliyor,
  // /sso/exchange yine de SSO_MERCHANT_INACTIVE döndü). Yoklama bölüm
  // açılışına bağlıdır: sayfa her açıldığında dış ağa çıkmak, ekranda hiç
  // bakılmayan bir bölüm için gereksiz bir istektir.
  const flexrixProbed = useRef(false);
  useEffect(() => {
    if (!openSections.flexrix || flexrixProbed.current) return;
    if (!flexrixLiveOf(settings)) return;
    flexrixProbed.current = true;
    // İstek burada yapılır: testFlexrix erken return'lerin altında tanımlı
    // ve bağımlılık listesine giremez.
    client.get('/admin/flexrix/status')
      .then(({ data }) => setFlexrixStatus(data))
      .catch(() => setFlexrixStatus(null));
  }, [openSections.flexrix, settings]);

  // Açık anahtar bölüm açıkken ve kayıtlı anahtar varken bir kez okunur.
  // Ayrı bir istek çünkü ayar yanıtı özel anahtarı hiç taşımaz.
  useEffect(() => {
    if (!openSections.flexrix || flexrixPublicKey) return;
    if (!flexrixKeyReadyOf(settings)) return;
    loadFlexrixPublicKey();
  }, [openSections.flexrix, settings, flexrixPublicKey, loadFlexrixPublicKey]);

  if (loading) {
    return (
      <div className="gc-state is-inline">
        <Loader2 size={20} className="gc-spin" />
        <p>Spor kontrolü yükleniyor…</p>
      </div>
    );
  }

  if (!settings) {
    return (
      <div className="gc-state">
        <AlertTriangle size={24} color="#fb7185" />
        <h2>Spor kontrolü yüklenemedi</h2>
        <p>Ayarlar okunamadı. Sayfayı yenileyin; sorun sürerse sunucu günlüğüne bakın.</p>
      </div>
    );
  }

  const availableSports = Array.from(new Set(competitions.map((item) => item.sport)));
  const orderedSports = mergeCatalogOrder(
    availableSports,
    settings.sportsbook_sport_order,
  );
  const availableFeeds = Array.from(new Set(competitions.map((item) => item.feed || 'sportsbook')));
  const activeFeed = availableFeeds.includes(catalogFeed)
    ? catalogFeed
    : (availableFeeds[0] || 'sportsbook');
  const feedOrderKey = FEED_ORDER_KEYS[activeFeed];
  // Sıralama ayarı kaynağa özeldir: taşıma yalnızca o kaynağın kodlarını
  // kapsamalı, yoksa iki listenin kodları birbirine karışır.
  const feedCompetitions = competitions
    .filter((item) => (item.feed || 'sportsbook') === activeFeed);
  const availableCompetitionCodes = feedCompetitions.map((item) => item.short_name);
  const orderedCompetitionCodes = mergeCatalogOrder(
    availableCompetitionCodes,
    settings[feedOrderKey],
  );
  const sportCompetitions = feedCompetitions
    .filter((item) => item.sport === catalogSport)
    .sort((left, right) => (
      orderedCompetitionCodes.indexOf(left.short_name)
      - orderedCompetitionCodes.indexOf(right.short_name)
    ));
  const sportCompetitionCodes = sportCompetitions.map((item) => item.short_name);
  const oddsapiRegions = csvList(settings.oddsapi_regions || 'eu');
  const oddsapiMarkets = csvList(settings.oddsapi_markets || 'h2h,spreads,totals');
  // Anahtarsız açılan kaynak backend'de kapalı sayılır; bunu söylemezsek
  // yönetici "açtım ama gelmiyor" ile karşılaşır.
  const missingKey = (enabledKey, secretKey) => (
    !!settings[enabledKey] && !settings[secretKey] && !settings[`${secretKey}_configured`]
  );
  const activeFeedCount = [
    settings.apifootball_enabled,
    settings.sportsbook_api_enabled,
    settings.oddsapi_enabled,
  ].filter(Boolean).length;
  const sportsOn = settings.sports_enabled !== false;
  // Varsayılan açıktır: alan hiç yazılmamış kurulumda değer tanımsız
  // gelir ve kapalı sayılsaydı panel yayındaki davranışı yanlış yazardı.
  const guestViewOn = settings.sports_guest_view_enabled !== false;

  // Betby dört şartın **birlikte** sağlanmasıyla yayına girer; biri eksikken
  // "açık" göstermek yöneticiyi açılmayan bir sayfaya güvendirirdi. Aynı karar
  // sunucuda da verilir (services/syncode.sportsbook_available) — iki taraf
  // ayrışırsa panel açık der, oyuncu 503 alır.
  const betbyConfigured = Boolean(
    String(settings.syncode_api_url || '').trim()
    && String(settings.syncode_agent_code || '').trim()
    && (settings.syncode_token_configured
        || String(settings.syncode_token || '').trim()),
  );
  const betbyLive = Boolean(
    settings.syncode_enabled
    && settings.syncode_sports_enabled
    && settings.syncode_callback_registered
    && betbyConfigured,
  );
  let betbyAlert = null;
  if (settings.syncode_sports_enabled && !betbyConfigured) {
    betbyAlert = 'Agent kodu ve token girilmeden spor kitabı açılmaz; oyuncu /spor sayfasında hata alır.';
  } else if (settings.syncode_sports_enabled && !settings.syncode_callback_registered) {
    betbyAlert = 'Bildirim adresi sağlayıcı panelinde tanımlanmadan servis açılamaz — aşağıdaki onay kutusunu işaretleyin.';
  } else if (settings.syncode_sports_enabled && !settings.syncode_enabled) {
    betbyAlert = 'SynCode servisi kapalı: spor kapsamı açık olsa da spor kitabı yayına girmez.';
  }
  // Adres tarayıcının origin'inden türetilemez: panel ayrı bir alan adına
  // taşındığında callback panelin adresine yazılırdı (bkz. site_address).
  const betbyCallbackBase = (
    settings.address_bases?.game || settings.address_bases?.site || ''
  );
  // Taban çözülemediğinde adres yalnızca yolu (``/api/flexrix``) gösteriyor
  // ve Kopyala düğmesi o yarım değeri panoya yazıyordu: yönetici sağlayıcıya
  // göreli bir adres yazarsa bütün bildirimler 404 olur ve arıza sessizdir.
  // Yarım adres göstermektense sebebini yazmak yeğdir.
  const callbackBaseMissing = !betbyCallbackBase;
  const betbyCallbackUrl = `${betbyCallbackBase}/api/syncode`;
  const copyBetbyCallback = async () => {
    try {
      await navigator.clipboard.writeText(betbyCallbackUrl);
      setBetbyCopied(true);
      setTimeout(() => setBetbyCopied(false), 2000);
    } catch {
      toast({ title: 'Kopyalanamadı', description: 'Adresi elle kopyalayın.' });
    }
  };

  // EveryMatrix'in "yayında mı" kararı sunucudan gelir (`available`): aynı
  // beş şartı orada da `sportsbook_available` okur ve iki taraf ayrışırsa
  // panel "yayında" der, oyuncu açılmayan bir sayfa görür.
  const emsportsLive = Boolean(emsports?.available);
  const emsportsProviderDown = Boolean(
    emsportsLive && emsportsStatus && !emsportsStatus.connected,
  );
  let emsportsAlert = null;
  if (emsports && emsports.sports_enabled !== false) {
    if (!String(emsports.sports_url || '').trim()
        || !String(emsports.merchant_key || '').trim()
        || !emsports.api_secret_configured) {
      emsportsAlert = 'Spor kitabı adresi, merchant key ve API secret girilmeden servis açılmaz; oyuncu /spor sayfasında hata alır.';
    } else if (!emsports.callback_registered) {
      emsportsAlert = 'Bildirim adresi sağlayıcı panelinde tanımlanmadan servis açılamaz — aşağıdaki onay kutusunu işaretleyin.';
    } else if (!emsports.enabled) {
      emsportsAlert = 'Servis kapalı: spor kapsamı açık olsa da spor kitabı yayına girmez.';
    }
  }
  const emsportsCallbackUrl = `${betbyCallbackBase}/api/emsports/callback`;
  const copyEmsportsCallback = async () => {
    try {
      await navigator.clipboard.writeText(emsportsCallbackUrl);
      setEmsportsCopied(true);
      setTimeout(() => setEmsportsCopied(false), 2000);
    } catch {
      toast({ title: 'Kopyalanamadı', description: 'Adresi elle kopyalayın.' });
    }
  };

  // Digitain'in "yayında mı" kararı da sunucudan gelir (`available`): aynı
  // beş şartı orada da `sportsbook_available` okur ve iki taraf ayrışırsa
  // panel "yayında" der, oyuncu açılmayan bir sayfa görür.
  const sportdigiLive = Boolean(sportdigi?.available);
  const sportdigiProviderDown = Boolean(
    sportdigiLive && sportdigiStatus && !sportdigiStatus.connected,
  );
  let sportdigiAlert = null;
  if (sportdigi && sportdigi.sports_enabled !== false) {
    if (!String(sportdigi.sports_url || '').trim()
        || !String(sportdigi.merchant_key || '').trim()
        || !sportdigi.api_secret_configured) {
      sportdigiAlert = 'Spor kitabı adresi, merchant key ve API secret girilmeden servis açılmaz; oyuncu /spor sayfasında hata alır.';
    } else if (!sportdigi.callback_registered) {
      sportdigiAlert = 'Bildirim adresi sağlayıcı panelinde tanımlanmadan servis açılamaz — aşağıdaki onay kutusunu işaretleyin.';
    } else if (!sportdigi.enabled) {
      sportdigiAlert = 'Servis kapalı: spor kapsamı açık olsa da spor kitabı yayına girmez.';
    }
  }
  const sportdigiCallbackUrl = `${betbyCallbackBase}/api/sportdigi/callback`;
  const copySportdigiCallback = async () => {
    try {
      await navigator.clipboard.writeText(sportdigiCallbackUrl);
      setSportdigiCopied(true);
      setTimeout(() => setSportdigiCopied(false), 2000);
    } catch {
      toast({ title: 'Kopyalanamadı', description: 'Adresi elle kopyalayın.' });
    }
  };

  // Exestruct'ın "yayında mı" kararı da sunucudan gelir (`available`).
  const exestructLive = Boolean(exestruct?.available);
  const exestructProviderDown = Boolean(
    exestructLive && exestructStatus && !exestructStatus.connected,
  );
  let exestructAlert = null;
  if (exestruct && exestruct.sports_enabled !== false) {
    if (!String(exestruct.agent_code || '').trim()
        || !exestruct.api_key_configured
        || !exestruct.api_secret_configured) {
      exestructAlert = 'Agent kodu, API Key ve API Secret girilmeden servis açılmaz; oyuncu /spor sayfasında hata alır.';
    } else if (!exestruct.callback_registered) {
      exestructAlert = 'Webhook adresi Exestruct panelinde tanımlanmadan servis açılamaz — aşağıdaki onay kutusunu işaretleyin.';
    } else if (!exestruct.enabled) {
      exestructAlert = 'Servis kapalı: spor kapsamı açık olsa da spor kitabı yayına girmez.';
    }
  }
  const exestructCallbackUrl = `${betbyCallbackBase}/api/exestruct/webhook`;

  // Viper'ın "yayında mı" kararı da sunucudan gelir (`available`).
  const viperLive = Boolean(viper?.available);
  const viperProviderDown = Boolean(
    viperLive && viperStatus && !viperStatus.connected,
  );
  let viperAlert = null;
  if (viper && viper.sports_enabled !== false) {
    if (!String(viper.api_url || '').trim()
        || !String(viper.api_key || '').trim()
        || !viper.api_secret_configured) {
      viperAlert = 'API adresi, API Key ve API Secret girilmeden servis açılmaz; oyuncu /spor sayfasında hata alır.';
    } else if (!(viper.books || []).length) {
      viperAlert = 'Açılacak spor kitabı seçilmedi — "Spor kitaplarını getir" ile katalogdan seçin.';
    } else if (!viper.callback_registered) {
      viperAlert = 'Cüzdan adresi Viper\'a yazılmadan servis açılamaz — "Sağlayıcıya kaydet" düğmesini kullanın.';
    } else if (!viper.enabled) {
      viperAlert = 'Servis kapalı: spor kapsamı açık olsa da spor kitabı yayına girmez.';
    }
  }
  const viperCallbackUrl = `${betbyCallbackBase}/api/viper/callback`;
  const copyViperCallback = async () => {
    try {
      await navigator.clipboard.writeText(viperCallbackUrl);
      setViperCopied(true);
      setTimeout(() => setViperCopied(false), 2000);
    } catch {
      toast({ title: 'Kopyalanamadı', description: 'Adresi elle kopyalayın.' });
    }
  };
  // Adres sağlayıcıya API ile yazılır; beyaz liste olduğu gibi korunur
  // (PUT iki alanı birden değiştirir, backend önce okur).
  const registerViperCallback = async () => {
    setViperBusy('callback');
    try {
      const { data } = await client.post('/admin/viper/callback', {
        callback_url: viperCallbackUrl,
      });
      const mark = (current) => ({
        ...(current || {}),
        callback_registered: true,
        available: Boolean(data.config?.available),
      });
      setViper(mark);
      setViperInitial(mark);
      toast({ title: 'Cüzdan adresi Viper\'a kaydedildi' });
    } catch (error) {
      toast({
        title: 'Cüzdan adresi kaydedilemedi',
        description: error.response?.data?.detail || 'İşlem başarısız',
      });
    } finally {
      setViperBusy('');
    }
  };
  const copyExestructCallback = async () => {
    try {
      await navigator.clipboard.writeText(exestructCallbackUrl);
      setExestructCopied(true);
      setTimeout(() => setExestructCopied(false), 2000);
    } catch {
      toast({ title: 'Kopyalanamadı', description: 'Adresi elle kopyalayın.' });
    }
  };

  // Flexrix beş şartın **birlikte** sağlanmasıyla yayına girer (servis açık,
  // spor kapsamı açık, kimlik tam, SSO anahtarı üretilmiş, sağlayıcı
  // panelindeki kayıt onaylı). Aynı karar sunucuda da verilir
  // (services/flexrix.sportsbook_available) — iki taraf ayrışırsa panel
  // "yayında" der, oyuncu açılmayan bir sayfa görür.
  const flexrixConfigured = flexrixConfiguredOf(settings);
  const flexrixKeyReady = flexrixKeyReadyOf(settings);
  const flexrixLive = flexrixLiveOf(settings);
  // Ayarlar eksiksiz olduğu hâlde sağlayıcının kendisi oturumu kabul
  // etmiyorsa (abonelik pasif, merchant tanınmıyor) panel bunu yeşil rozetle
  // gizlememelidir; durum bölüm açılışındaki sessiz yoklamadan gelir.
  const flexrixProviderDown = Boolean(
    flexrixLive && flexrixStatus && !flexrixStatus.connected,
  );
  let flexrixAlert = null;
  if (settings.flexrix_sports_enabled && !flexrixConfigured) {
    flexrixAlert = 'Merchant key, API secret ve spor kitabı adresi girilmeden servis açılmaz; oyuncu /spor sayfasında hata alır.';
  } else if (settings.flexrix_sports_enabled && !flexrixKeyReady) {
    flexrixAlert = 'SSO özel anahtarı tanımlanmamış: Flexrix hub panelinden alınan özel anahtarı aşağıya yapıştırın.';
  } else if (settings.flexrix_sports_enabled && !settings.flexrix_callback_registered) {
    flexrixAlert = 'Bildirim adresi ve SSO genel anahtarı sağlayıcı panelinde tanımlanmadan servis açılamaz — aşağıdaki onay kutusunu işaretleyin.';
  } else if (settings.flexrix_sports_enabled && !settings.flexrix_enabled) {
    flexrixAlert = 'BetConstruct servisi kapalı: spor kapsamı açık olsa da spor kitabı yayına girmez.';
  } else if (flexrixProviderDown) {
    // Ayarlar tamam, sağlayıcı reddediyor. Oyuncu bu durumda /spor sayfasında
    // "sağlayıcı oturumu kabul etmiyor" hatası alır; panelde tek işareti
    // yeşil bir rozet olmamalı.
    flexrixAlert = `Ayarlar tamam ama spor kitabı açılmıyor — ${flexrixStatusTitle(flexrixStatus).toLowerCase()}. Oyuncu /spor sayfasında hata alır; çözüm Flexrix tarafındadır.`;
  } else if (flexrixLive && betbyLive) {
    flexrixAlert = 'İki hazır spor kitabı da açık: /spor sayfasında BetConstruct çizilir, Betby devre dışı kalır.';
  }
  const flexrixCallbackUrl = `${betbyCallbackBase}/api/flexrix`;
  const flexrixWebhookUrl = `${betbyCallbackBase}/api/flexrix/webhook`;
  const copyFlexrix = async (value, tag) => {
    try {
      await navigator.clipboard.writeText(value);
      setFlexrixCopied(tag);
      setTimeout(() => setFlexrixCopied(''), 2000);
    } catch {
      toast({ title: 'Kopyalanamadı', description: 'Adresi elle kopyalayın.' });
    }
  };
  // SSO özel anahtarı ayrı uçla kaydedilir: PEM doğrulanarak yazılır,
  // yanlış biçim 400 döner. Anahtar değişince onay kutusu düşer.
  const verifyFlexrixPublicKey = async () => {
    const pem = flexrixPublicKeyDraft.trim();
    if (!pem) {
      toast({
        title: 'Açık anahtar boş',
        description: 'Flexrix hub panelindeki Açık Anahtar değerini yapıştırın.',
      });
      return;
    }
    setFlexrixBusy('pubkey');
    try {
      const { data } = await client.post('/admin/flexrix/sso-key/verify', {
        public_key: pem,
      });
      setFlexrixKeyMatch(data.matches ? 'match' : 'mismatch');
      toast({
        title: data.matches ? 'Anahtar çifti eşleşiyor' : 'Anahtar çifti EŞLEŞMİYOR',
        description: data.matches
          ? 'Hub’daki açık anahtar buradaki özel anahtarla aynı çiftten.'
          : 'Hub’da başka bir çift duruyor: hub’daki özel anahtarı buraya yapıştırın.',
      });
    } catch (error) {
      setFlexrixKeyMatch('');
      toast({
        title: 'Karşılaştırma yapılamadı',
        description: error.response?.data?.detail || 'İstek başarısız',
      });
    } finally {
      setFlexrixBusy('');
    }
  };

  const saveFlexrixSsoKey = async () => {
    const pem = flexrixSsoKeyDraft.trim();
    if (!pem) {
      toast({ title: 'Özel anahtar boş', description: 'Flexrix hub panelinden kopyaladığınız özel anahtarı yapıştırın.' });
      return;
    }
    setFlexrixBusy('ssokey');
    try {
      const { data } = await client.post('/admin/flexrix/sso-key', {
        private_key: pem,
        algorithm: settings.flexrix_jwt_algorithm || 'EdDSA',
      });
      setSettings((current) => ({
        ...current,
        flexrix_jwt_algorithm: data.algorithm,
        flexrix_jwt_key_configured: true,
        flexrix_callback_registered: false,
      }));
      setInitial((current) => ({
        ...current,
        flexrix_jwt_algorithm: data.algorithm,
        flexrix_jwt_key_configured: true,
        flexrix_callback_registered: false,
      }));
      setFlexrixSsoKeyDraft('');
      setFlexrixPublicKey(data.public_key || '');
      setFlexrixKeyMatch('');
      toast({
        title: 'SSO özel anahtarı kaydedildi',
        description: 'Anahtar doğrulandı ve kaydedildi; onay kutusu sıfırlandı.',
      });
    } catch (error) {
      toast({
        title: 'Anahtar kaydedilemedi',
        description: error.response?.data?.detail || 'İstek başarısız',
      });
    } finally {
      setFlexrixBusy('');
    }
  };
  // Tema Kaydet gövdesiyle gitmez: görünüm sağlayıcının kaydındadır ve
  // ayrı bir çağrıyla uygulanır. Ayar satırı yalnızca son uygulanan değerin
  // aynasıdır — "kaydettim ama değişmedi" durumu bu yüzden doğmaz.
  const applyFlexrixTheme = async () => {
    setFlexrixBusy('theme');
    try {
      const { data } = await client.patch('/admin/flexrix/theme', {
        theme: settings.flexrix_theme || 'flexrix-red',
      });
      setSettings((current) => ({ ...current, flexrix_theme: data.theme }));
      setInitial((current) => ({ ...current, flexrix_theme: data.theme }));
      toast({ title: 'Tema uygulandı', description: data.theme });
    } catch (error) {
      toast({
        title: 'Tema uygulanamadı',
        description: error.response?.data?.detail || 'İstek başarısız',
      });
    } finally {
      setFlexrixBusy('');
    }
  };
  // Kupon webhook'u sağlayıcıya API ile kurulur; adres sunucudan türer,
  // panel yalnızca tetikler.
  const registerFlexrixWebhook = async () => {
    setFlexrixBusy('webhook');
    try {
      const { data } = await client.put('/admin/flexrix/webhooks');
      toast({
        title: 'Kupon webhook’u kaydedildi',
        description: data.webhook_url || flexrixWebhookUrl,
      });
    } catch (error) {
      toast({
        title: 'Webhook kaydedilemedi',
        description: error.response?.data?.detail || 'İstek başarısız',
      });
    } finally {
      setFlexrixBusy('');
    }
  };
  // Bağlantı testi gerçek bir katalog çağrısıdır: kimliği, imzayı ve
  // aboneliğin aktifliğini tek seferde ortaya çıkarır, işlem üretmez.
  // ``silent`` bölüm açılışındaki tek seferlik yoklama içindir: yönetici
  // istemediği bir toast görmemeli, ama rozetin doğruyu söylemesi için
  // sonuç yine de alınmalı.
  const testFlexrix = async (silent = false) => {
    setFlexrixBusy('status');
    try {
      const { data } = await client.get('/admin/flexrix/status');
      setFlexrixStatus(data);
      if (silent) return;
      toast({
        title: flexrixStatusTitle(data),
        description: data.connected
          ? `${data.sport_count} spor dalı listelendi.`
          : (data.error || 'Sağlayıcı yanıt vermedi'),
      });
    } catch (error) {
      if (silent) { setFlexrixStatus(null); return; }
      setFlexrixStatus(null);
      toast({
        title: 'BetConstruct bağlantısı kurulamadı',
        description: error.response?.data?.detail || 'İstek başarısız',
      });
    } finally {
      setFlexrixBusy('');
    }
  };

  // Ödeme zinciri tükenmiş (dead-letter) kuponlar. 18. denemeden sonra
  // (~43 saat) sağlayıcı denemeyi durdurur ve kazanç oyuncuya **hiç**
  // ödenmez; kendiliğinden düzelmez, yeniden kuyruğa almak gerekir.
  const loadFlexrixDead = async () => {
    setFlexrixBusy('dead');
    try {
      const { data } = await client.get('/admin/flexrix/credits/dead');
      const items = data.items || [];
      setFlexrixDead(items);
      toast({
        title: items.length
          ? `${items.length} kuponun kazancı ödenememiş`
          : 'Ödenemeyen kazanç yok',
        description: items.length
          ? 'Yeniden kuyruğa almak için satırdaki düğmeyi kullanın.'
          : 'Sağlayıcının ödeme kuyruğu temiz.',
      });
    } catch (error) {
      setFlexrixDead(null);
      toast({
        title: 'Ödenemeyen kazançlar okunamadı',
        description: error.response?.data?.detail || 'İstek başarısız',
      });
    } finally {
      setFlexrixBusy('');
    }
  };

  const retryFlexrixCredit = async (betId) => {
    setFlexrixBusy(`retry:${betId}`);
    try {
      await client.post(`/admin/flexrix/credits/${betId}/retry-credit`);
      toast({
        title: 'Kupon yeniden kuyruğa alındı',
        description: `#${betId} ödemesi aynı işlem kimliğiyle yeniden `
          + 'gönderilecek; çift ödeme olmaz.',
      });
      setFlexrixDead((rows) => (rows || []).filter(
        (row) => String(row.id) !== String(betId)));
    } catch (error) {
      toast({
        title: 'Kupon yeniden kuyruğa alınamadı',
        description: error.response?.data?.detail || 'İstek başarısız',
      });
    } finally {
      setFlexrixBusy('');
    }
  };

  // Maç durumu bildirimi sağlayıcı tarafında tutulur (Partner API):
  // bizde bir ayar alanı yoktur, bu yüzden durum okunur ve yazılır.
  const loadFlexrixMatchPush = async () => {
    setFlexrixBusy('match');
    try {
      const { data } = await client.get('/admin/flexrix/match-status');
      setFlexrixMatchPush(!!data.enabled);
    } catch (error) {
      setFlexrixMatchPush(null);
      toast({
        title: 'Maç durumu bildirimi okunamadı',
        description: error.response?.data?.detail || 'İstek başarısız',
      });
    } finally {
      setFlexrixBusy('');
    }
  };

  const setFlexrixMatchPushValue = async (enabled) => {
    setFlexrixBusy('match');
    try {
      await client.put('/admin/flexrix/match-status', { enabled });
      setFlexrixMatchPush(enabled);
      toast({
        title: enabled
          ? 'Maç durumu bildirimi açıldı'
          : 'Maç durumu bildirimi kapatıldı',
        description: enabled
          ? 'Sağlayıcı açık bahsimiz olan maçların skorunu '
            + '/api/flexrix/match-status adresine gönderecek.'
          : 'Skor bildirimi durduruldu.',
      });
    } catch (error) {
      toast({
        title: 'Maç durumu bildirimi değiştirilemedi',
        description: error.response?.data?.detail || 'İstek başarısız',
      });
    } finally {
      setFlexrixBusy('');
    }
  };

  // Bölümün kendi kaydet şeridi. EveryMatrix ve Digitain'in kendi düğmesi
  // vardı, `settings` kaydına yazan bölümlerin yoktu: yönetici aynı ekranda
  // iki farklı kaydetme biçimi görüyordu. Şerit hepsinde aynı yerde durur ve
  // yalnızca kendi alanlarını yazar.
  const SectionSave = ({ section, children }) => {
    const keys = SECTION_SETTING_KEYS[section] || [];
    const pending = dirtyKeys.filter((key) => keys.includes(key)).length;
    return (
      <div className="gc-sect-save">
        <span className="gc-foot-note">
          {pending
            ? `${pending} alan değiştirildi, henüz kaydedilmedi.`
            : 'Kaydedilmemiş değişiklik yok.'}
        </span>
        {children}
        <button
          type="button"
          onClick={() => save(keys)}
          disabled={saving || !pending}
          className="gc-btn is-sm is-primary"
        >
          {saving ? <Loader2 size={13} className="gc-spin" /> : <Save size={13} />}
          Kaydet
        </button>
      </div>
    );
  };

  const allOpen = SECTION_IDS.every((id) => openSections[id]);

  // EveryMatrix, Digitain, Exestruct ve Viper `provider_configs` satırına yazar,
  // kalanlar `settings`'e. Alttaki şerit bir süre yalnızca ikincisini
  // sayıyordu: bölüm katlıyken başlıktaki anahtar çevrildiğinde şerit
  // "Kaydedilmemiş değişiklik yok" diyor ve değişiklik hiç kaydedilemiyordu.
  // Şerit bütün kaynakları birlikte sayar ve her birini kendi ucuna yazar —
  // yeni bir `provider_configs` kartı buraya eklenmezse aynı arıza döner.
  const pendingTotal = dirtyKeys.length + emsportsDirty.length
    + sportdigiDirty.length + exestructDirty.length + viperDirty.length;
  const savingAny = saving || emsportsBusy === 'save' || sportdigiBusy === 'save'
    || exestructBusy === 'save' || viperBusy === 'save';
  const saveAll = async () => {
    if (!pendingTotal) {
      toast({ title: 'Kaydedilecek değişiklik yok' });
      return;
    }
    if (dirtyKeys.length) await save();
    if (emsportsDirty.length) await saveEmsports();
    if (sportdigiDirty.length) await saveSportdigi();
    if (exestructDirty.length) await saveExestruct();
    if (viperDirty.length) await saveViper();
  };

  return (
    <section className="gc-panel">
      {showRest && (
      <div className="gc-panel-head">
        <span className="gc-card-icon"><Trophy size={19} /></span>
        <div className="gc-panel-title">
          <h2>Spor ve Cashout Kontrolü</h2>
          <p>Spor görünürlüğünü, oran beslemesini, marjları ve sonuç girişlerini yönetin.</p>
        </div>
        <button
          type="button"
          onClick={() => setAllSections(!allOpen)}
          className="gc-btn is-sm"
        >
          <ChevronDown size={12} className={allOpen ? 'rotate-180' : ''} />
          {allOpen ? 'Tümünü kapat' : 'Tümünü aç'}
        </button>
      </div>
      )}

      <div className="gc-panel-body">
        {/* Sitedeki spor bölümünün ana anahtarı: katlanmaz, her zaman görünür. */}
        {showRest && (
        <div>
          <div className={`gc-master ${sportsOn ? 'is-on' : ''}`}>
            <span className="gc-master-icon"><Activity size={17} /></span>
            <div className="gc-master-text">
              <h3>Spor bölümü {sportsOn ? 'açık' : 'kapalı'}</h3>
              <p>Kapatıldığında spor menüsü, ana sayfa kartı ve spor erişimi gizlenir.</p>
            </div>
            <CountBadge>{activeFeedCount}/3 kaynak açık</CountBadge>
            <Switch
              checked={sportsOn}
              onChange={(value) => update('sports_enabled', value)}
              label="Spor bölümünü aç/kapat"
            />
          </div>

          {/* Misafir görünümü **bütün** spor kaynakları için tek ayardır: sitenin
              kendi oran beslemesi, Betby ve BetConstruct aynı kapıyı okur. Sağlayıcı
              başına ayrı bir kutu olsaydı biri açık biri kapalı kalır ve aynı site
              iki farklı şey söylerdi. */}
          {sportsOn && (
            <div className={`gc-master ${guestViewOn ? 'is-on' : ''}`}>
              <span className="gc-master-icon"><Eye size={17} /></span>
              <div className="gc-master-text">
                <h3>Giriş yapmadan görüntüleme {guestViewOn ? 'açık' : 'kapalı'}</h3>
                <p>
                  {guestViewOn
                    ? 'Ziyaretçi oranları ve maçları giriş yapmadan görür; kupon '
                      + 'yapmak için giriş gerekir. Hazır spor kitapları (Betby, '
                      + 'BetConstruct) izleme modunda açılır.'
                    : 'Spor sayfası yalnızca giriş yapan üyelere açılır; '
                      + 'ziyaretçiye giriş kartı gösterilir.'}
                </p>
              </div>
              <Switch
                checked={guestViewOn}
                onChange={(value) => update('sports_guest_view_enabled', value)}
                label="Giriş yapmadan görüntülemeyi aç/kapat"
              />
            </div>
          )}
        </div>
        )}

      {/* Betby bir oran kaynağı değildir: bahsin tamamını (oran, kupon,
          sonuçlandırma) sağlayıcı yürütür ve açıkken aşağıdaki üç kaynak hiç
          kullanılmaz. Bu yüzden listenin içine değil üstüne konur. */}

      {!view && (
      <div className="gc-grouplabel">
        Hazır spor kitapları
        <span>Biri açıkken sitenin kendi oran beslemesi devre dışı kalır; birden çoğu yayındayken oyuncu çerçevenin üstündeki şeritten geçiş yapar.</span>
      </div>
      )}

      <div className="gc-sects">
        {showBook('flexrix') && (
        <Section
          tone={TONES.flexrix}
          icon={Trophy}
          title="BetConstruct Sportsbook"
          subtitle="Flexrix · Native · para bizde, bahis sağlayıcıda"
          tags={['Hazır sportsbook', 'SSO JWT', 'HMAC cüzdan']}
          badge={(
            <StatusBadge
              enabled={flexrixLive}
              warn={flexrixProviderDown}
              intent={
                !!settings.flexrix_sports_enabled && !!settings.flexrix_enabled
              }
            />
          )}
          control={(
            <Switch
              tone={TONES.flexrix}
              checked={
                !!settings.flexrix_sports_enabled && !!settings.flexrix_enabled
              }
              onChange={(value) => updateMany({
                flexrix_sports_enabled: value,
                flexrix_enabled: value,
              })}
              label="Spor kitabını yayına al / kaldır (kapalıyken sayfa siteden kalkar ve yeni bahis alınmaz)"
            />
          )}
          open={!!openSections.flexrix}
          onToggle={() => toggleSection('flexrix')}
          alert={flexrixAlert}
        >
          <ServiceSwitchNote />
          <p className="text-xs text-slate-500 mb-3">
            Açıkken <code className="text-rose-400">/spor</code> sayfası BetConstruct
            çerçevesini çizer; kapatıldığında sitenin kendi oran beslemesine geri
            döner. <strong>İki hazır spor kitabı aynı anda yayınlanmaz</strong> —
            ikisi de açıksa BetConstruct öndedir.
          </p>
          <p className="text-xs text-slate-500 mb-3">
            Oyuncu imzalı bir SSO jetonuyla sağlayıcının SPA'sına düşer; bahis,
            kazanç ve iptal bizim uçlarımıza HMAC imzalı olarak bildirilir ve
            üyenin finans listesinde görünür. Cashout, özel oran ve elle
            sonuçlandırma bu kaynakta çalışmaz; hepsi sağlayıcının kendi
            kurallarına tabidir. <strong>Komisyon</strong> stake yüzdesi olarak
            Flexrix MerchantWallet'ınızdan anlık düşer — bakiye biterse bahisler
            reddedilir.
          </p>
          <div className="gc-fields">
            <Field label="API URL">
              <input
                value={settings.flexrix_api_url || ''}
                onChange={(event) => update('flexrix_api_url', event.target.value)}
                className="gc-input"
                placeholder="https://api.upaflex.online"
              />
            </Field>
            <Field label="Spor kitabı adresi (SPA / iframe)">
              <input
                value={settings.flexrix_sports_url || ''}
                onChange={(event) => update('flexrix_sports_url', event.target.value)}
                className="gc-input"
                placeholder="https://spor.siteniz.com"
              />
              <PanelDomainLeak host={settings.flexrix_sports_url_exposes_panel} />
            </Field>
            <Field label="Merchant Key">
              <input
                value={settings.flexrix_merchant_key || ''}
                onChange={(event) => update('flexrix_merchant_key', event.target.value)}
                className="gc-input"
                placeholder="FLX_XXXXXXXX"
              />
            </Field>
            <Field label="Para Birimi (oyuncu cüzdanı)">
              <input
                value={settings.flexrix_currency || ''}
                onChange={(event) => update(
                  'flexrix_currency', event.target.value.toUpperCase())}
                className="gc-input"
                placeholder="TRY"
              />
            </Field>
            <SecretField
              label="API Secret"
              value={settings.flexrix_api_secret}
              configured={settings.flexrix_api_secret_configured}
              placeholder="Cüzdan callback'lerinin ve giden imzaların anahtarı"
              onChange={(value) => update('flexrix_api_secret', value)}
            />
          </div>

          <div className="gc-box is-tone">
            <div className="gc-box-head">
              <div>
                <h3>Cüzdan bildirim adresi</h3>
                <p>Sağlayıcı panelinde <b>callback_url</b> alanına yazılacak <b>taban</b> adres.</p>
              </div>
            </div>
            {callbackBaseMissing ? <CallbackBaseMissing /> : (
              <div className="gc-copy">
                <code className="gc-copy-url">{flexrixCallbackUrl}</code>
                <button
                  type="button"
                  onClick={() => copyFlexrix(flexrixCallbackUrl, 'callback')}
                  className="gc-btn is-sm"
                >
                  {flexrixCopied === 'callback' ? 'Kopyalandı' : 'Kopyala'}
                </button>
              </div>
            )}
            <div className="text-[11px] text-slate-500 mt-1">
              Sona <code>/balance</code>, <code>/debit</code>, <code>/credit</code> ve
              <code> /rollback</code> eklerini sağlayıcı kendisi koyar — siz eklemeyin.
            </div>
            <label className="gc-check">
              <input
                type="checkbox"
                checked={!!settings.flexrix_callback_registered}
                onChange={(event) => update(
                  'flexrix_callback_registered', event.target.checked)}
              />
              <span>
                Callback adresini Flexrix panelinde tanımladım ve SSO özel anahtarını aşağıya yapıştırdım
                <span className="block text-[11px] text-slate-500 mt-0.5">
                  Onaylanmadan servis açılamaz: adres yazılmazsa bahis ve kazanç
                  bize hiç bildirilmez, özel anahtar kaydedilmezse oyuncu spor
                  kitabına hiç giremez. Kaydedilmiş onay sonraki kayıtlarda
                  korunur; sağlayıcı panelindeki kayıt artık geçerli değilse
                  kutuyu kaldırın.
                </span>
              </span>
            </label>
          </div>

          {/* SSO: anahtar çifti Flexrix hub panelinde üretilir. Özel anahtar
              buraya yapıştırılır, açık anahtar sağlayıcıda kalır. */}
          <div className="gc-box">
            <div className="gc-box-head">
              <div>
                <h3>SSO anahtarı</h3>
                <p>
                  {flexrixKeyReady
                    ? 'Özel anahtar kaydedilmiş.'
                    : 'Özel anahtar yok — spor kitabı açılmaz.'}
                </p>
              </div>
              <select
                value={settings.flexrix_jwt_algorithm || 'EdDSA'}
                onChange={(event) => update('flexrix_jwt_algorithm', event.target.value)}
                className="gc-input is-md gc-spacer"
                aria-label="Jeton imza algoritması"
              >
                {FLEXRIX_JWT_ALGORITHMS.map((option) => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            </div>
            <textarea
              value={flexrixSsoKeyDraft}
              onChange={(event) => setFlexrixSsoKeyDraft(event.target.value)}
              placeholder="Sağlayıcının verdiği özel anahtarı buraya yapıştırın — PEM, OpenSSH, JWK ya da düz base64 kabul edilir"
              className="gc-input is-mono"
              rows={4}
            />
            <div className="gc-btns">
              <button
                type="button"
                onClick={saveFlexrixSsoKey}
                disabled={flexrixBusy === 'ssokey' || !flexrixSsoKeyDraft.trim()}
                className="gc-btn is-sm"
              >
                {flexrixBusy === 'ssokey' ? 'Kaydediliyor…' : 'Anahtarı Kaydet'}
              </button>
              {flexrixKeyReady && (
                <span className="text-[11px] text-emerald-400">✓ Kayıtlı anahtar mevcut</span>
              )}
            </div>
            <div className="text-[11px] text-slate-500 mt-2">
              Anahtar çifti Flexrix hub panelinde üretilir (<b>Sportsbook Ayarları
              → SSO Anahtar Çifti</b>). Orada <b>Özel Anahtar</b>ı kopyalayıp
              buraya yapıştırın; <b>Açık Anahtar</b> sağlayıcıda kalır, ona
              dokunmanız gerekmez. Yeni anahtar kaydedildiğinde onay kutusu
              sıfırlanır.
              <span className="block mt-1">
                <b>Biçim önemli değil:</b> sağlayıcı size ne verdiyse olduğu gibi
                yapıştırın — PEM (<code>BEGIN PRIVATE KEY</code> ya da
                <code> BEGIN RSA PRIVATE KEY</code>), OpenSSH bloğu, JWK (JSON)
                veya başlıksız base64/hex. Hepsi kaydedilirken tek biçime
                çevrilir. <b>Algoritmayı da anahtarın kendisi belirler</b>;
                yandaki seçim yalnızca ipucudur, yanlış seçilse bile
                düzeltilir.
              </span>
            </div>

            {/* Açık anahtar sağlayıcıda durur ve bize gönderilmez; burada
                gösterilmesinin tek sebebi karşılaştırmadır. Yanlış çift
                yapıştırıldığında sağlayıcı jetonu reddeder, oyuncu spor
                kitabına giremez ve hata mesajı sebebi ayırt etmez. */}
            {flexrixKeyReady && (
              <div className="gc-subblock">
                <div className="gc-box-head">
                  <div>
                    <h3>Açık anahtar</h3>
                    <p>Kayıtlı özel anahtardan türetildi · hub’daki değerle aynı olmalı.</p>
                  </div>
                  {flexrixPublicKey && (
                    <button
                      type="button"
                      onClick={() => copyFlexrix(flexrixPublicKey, 'pubkey')}
                      className="gc-btn is-sm gc-spacer"
                    >
                      {flexrixCopied === 'pubkey' ? 'Kopyalandı' : 'Kopyala'}
                    </button>
                  )}
                </div>
                <pre className="gc-input is-mono gc-pre">
                  {flexrixPublicKey || 'Yükleniyor…'}
                </pre>
                <div className="text-[11px] text-slate-500 mt-2 mb-1">
                  Emin olmak isterseniz hub’daki <b>Açık Anahtar</b> değerini
                  yapıştırıp karşılaştırın — eşleşmiyorsa hub’da başka bir çift
                  duruyor demektir.
                </div>
                <textarea
                  value={flexrixPublicKeyDraft}
                  onChange={(event) => {
                    setFlexrixPublicKeyDraft(event.target.value);
                    setFlexrixKeyMatch('');
                  }}
                  placeholder="Hub panelindeki Açık Anahtar (PEM) — karşılaştırmak için"
                  className="gc-input is-mono"
                  rows={3}
                />
                <div className="gc-btns">
                  <button
                    type="button"
                    onClick={verifyFlexrixPublicKey}
                    disabled={flexrixBusy === 'pubkey' || !flexrixPublicKeyDraft.trim()}
                    className="gc-btn is-sm"
                  >
                    {flexrixBusy === 'pubkey' ? 'Karşılaştırılıyor…' : 'Hub’daki anahtarla karşılaştır'}
                  </button>
                  {flexrixKeyMatch === 'match' && (
                    <span className="text-[11px] text-emerald-400">
                      ✓ Aynı çift — anahtar tarafında sorun yok
                    </span>
                  )}
                  {flexrixKeyMatch === 'mismatch' && (
                    <span className="text-[11px] text-rose-400">
                      ✗ Farklı çift — hub’daki özel anahtarı yukarıya yapıştırın
                    </span>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Bahis lifecycle webhook'u opsiyoneldir ve ayrı bir sırla imzalanır:
              para hareketi taşımaz, kuponun içeriğini (maç, oran, sonuç) getirir.
              Kapalıyken kupon detayı sağlayıcının bahis ucundan okunur. */}
          <div className="gc-fields">
            <SecretField
              label="Webhook Secret (kupon bildirimi · opsiyonel)"
              value={settings.flexrix_webhook_secret}
              configured={settings.flexrix_webhook_secret_configured}
              placeholder="En az 16 karakter"
              onChange={(value) => update('flexrix_webhook_secret', value)}
            />
            {/* Bu adresin sağlayıcı panelinde bir alanı YOKTUR: kupon
                webhook'u yalnızca API ile kurulur. Bu yüzden düğme adresi
                sağlayıcıya kendisi kaydeder — kopyalayıp elle yazılacak bir
                yer aranırsa bulunamaz. */}
            <Field label="Kupon webhook adresi (sağlayıcıya API ile kaydedilir)" full>
              {callbackBaseMissing ? <CallbackBaseMissing /> : (
                <div className="gc-copy">
                  <code className="gc-copy-url">{flexrixWebhookUrl}</code>
                  <button
                    type="button"
                    onClick={() => copyFlexrix(flexrixWebhookUrl, 'webhook')}
                    className="gc-btn is-sm"
                  >
                    {flexrixCopied === 'webhook' ? 'Kopyalandı' : 'Kopyala'}
                  </button>
                  <button
                    type="button"
                    onClick={registerFlexrixWebhook}
                    disabled={flexrixBusy === 'webhook'}
                    className="gc-btn is-sm is-tone"
                  >
                    {flexrixBusy === 'webhook' ? 'Kaydediliyor…' : 'Sağlayıcıya kaydet'}
                  </button>
                </div>
              )}
              <span className="gc-hint">
                Sırrı yukarıya yazıp ayarları kaydedin, sonra bu düğmeye basın.
                Flexrix panelinde webhook alanı yoktur; adres yalnızca buradan
                tanımlanır.
              </span>
            </Field>
          </div>

          <Field
            label="Spor kitabı teması"
            hint="Tema Kaydet ile gitmez: sağlayıcıya ayrı bir çağrıyla uygulanır."
          >
            <div className="gc-inline">
              <select
                value={settings.flexrix_theme || 'flexrix-red'}
                onChange={(event) => update('flexrix_theme', event.target.value)}
                className="gc-input is-lg"
              >
                {FLEXRIX_THEMES.map((option) => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
              <button
                type="button"
                onClick={applyFlexrixTheme}
                disabled={flexrixBusy === 'theme'}
                className="gc-btn is-sm"
              >
                {flexrixBusy === 'theme' ? 'Uygulanıyor…' : 'Temayı sağlayıcıya uygula'}
              </button>
            </div>
          </Field>

          <label className="gc-check">
            <input
              type="checkbox"
              checked={!!settings.flexrix_freebet_enabled}
              onChange={(event) => update('flexrix_freebet_enabled', event.target.checked)}
            />
            <span>
              Serbest bahis (freebet) kabul edilsin
              <span className="block text-[11px] text-slate-500 mt-0.5">
                Serbest bahiste sağlayıcı bahsi bize hiç bildirmez, kazananı
                yalnızca kâr tutarıyla yatırır. Kapalıyken bu bahissiz kazanç
                reddedilir — uydurma bir kupon numarasıyla kasadan para
                çıkarılmasın diye. Freebet dağıtacaksanız açın.
              </span>
            </span>
          </label>

          <div className="gc-btns">
            <button
              type="button"
              onClick={() => testFlexrix()}
              disabled={flexrixBusy === 'status'}
              className="gc-btn is-sm"
            >
              {flexrixBusy === 'status' ? 'Deneniyor…' : 'Bağlantıyı Test Et'}
            </button>
          </div>
          {flexrixStatus && (
            <div className="text-[11px] text-slate-400 mt-2">
              {flexrixStatus.connected
                ? `Bağlantı çalışıyor · ${flexrixStatus.sport_count} spor dalı · merchant ${flexrixStatus.merchant_key || '—'}`
                : `${flexrixStatusTitle(flexrixStatus)}: ${flexrixStatus.error || 'sağlayıcı yanıt vermedi'}`}
              {/* Abonelik kapalıyken yapılacak iş bizde değil sağlayıcıdadır;
                  yönetici anahtar aramasın diye açıkça yazılır. */}
              {flexrixStatus.merchant_known && !flexrixStatus.active && (
                <div className="text-amber-400 mt-1">
                  Merchant key tanınıyor, imza kabul ediliyor — yapılacak tek
                  şey Flexrix hub panelinden sportsbook aktivasyonunu
                  tamamlatmak. Panelde değiştirilecek bir ayar yok.
                </div>
              )}
              {/* Kenar katmanı reddinde iş sağlayıcıda değil bizdedir; aksi
                  hâlde yönetici anahtar/abonelik aramaya başlar. */}
              {flexrixStatus.error_code === 'IP_NOT_ALLOWED' && (
                <div className="text-amber-400 mt-1">
                  İstek Flexrix'e ulaşmadan Cloudflare tarafından kesildi —
                  anahtarla ilgisi yok. Sunucunun çıkış IPv4 adresi
                  sağlayıcının listesinde olmalı; adres değiştiyse Flexrix'e
                  bildirin.
                </div>
              )}
              {flexrixStatus.error_code === 'RATE_LIMITED' && (
                <div className="text-amber-400 mt-1">
                  Sağlayıcı istek sınırına takıldı. Bu geçicidir; spor kitabı
                  kapatılmaz, birkaç saniye sonra yeniden denenir.
                </div>
              )}
              {/* "Çok mu istek atıyoruz" sorusunun yanıtı. Sağlayıcı kotayı
                  sayıyla değil davranışla tarif ediyor ("yüzlerce/saniye"),
                  bu yüzden hacim ölçülmeden değerlendirilemez. Ortalama bir
                  bursu gizlediği için en yoğun dakika ayrıca yazılır. */}
              {flexrixStatus.traffic && (
                <div className="text-slate-400 mt-1">
                  Giden istek: son {flexrixTrafficWindow(flexrixStatus.traffic)}
                  {' '}<b>{flexrixStatus.traffic.total}</b> istek · dakikada
                  ortalama <b>{flexrixStatus.traffic.per_minute}</b> · en yoğun
                  dakika <b>{flexrixStatus.traffic.peak_minute}</b>
                  {flexrixStatus.traffic.top_paths?.length > 0 && (
                    <> · en çok {flexrixStatus.traffic.top_paths
                      .map(([path, n]) => `${path} (${n})`).join(', ')}</>
                  )}
                </div>
              )}
              {/* Kenar/kota reddi log'a yazılır ama panelde oturan yöneticinin
                  log'a erişimi yoktur; "sürekli rate limit yiyoruz" iddiası
                  bu yüzden haftalarca doğrulanamadı. Sayaç süreç belleğindedir
                  ve yeniden başlatmada sıfırlanır — kalıcı kayıt log'dur. */}
              {flexrixStatus.incidents?.total > 0 && (
                <div className="text-amber-400 mt-1">
                  Son {flexrixStatus.incidents.window_hours} saatte{' '}
                  {flexrixStatus.incidents.truncated ? 'en az ' : ''}
                  <b>{flexrixStatus.incidents.total}</b> sağlayıcı arızası
                  {Object.entries(flexrixStatus.incidents.by_code || {}).length > 0 && (
                    <> ({Object.entries(flexrixStatus.incidents.by_code)
                      .map(([code, n]) => `${code}: ${n}`).join(' · ')})</>
                  )}
                  {flexrixStatus.incidents.last_at && (
                    <> — sonuncusu{' '}
                      {new Date(flexrixStatus.incidents.last_at).toLocaleString('tr-TR')}
                      {flexrixStatus.incidents.last_path
                        ? ` · ${flexrixStatus.incidents.last_path}` : ''}
                    </>
                  )}
                  . Sayaç sunucu yeniden başlayınca sıfırlanır; tam kayıt
                  sunucu log'undadır.
                </div>
              )}
              {/* Komisyon pre-paid cüzdandan kesilir. Boşaldığında sağlayıcı
                  her bahsi oyuncunun kuponuna ``StatusCode -9`` ile reddeder
                  ve **bizim hiçbir ucumuza istek gelmez**: düşük bakiye
                  bildiren bir webhook da yok. Panelde görünmezse arıza
                  "spor bahsi çalışmıyor" diye bildirilir ve hiçbir yerde iz
                  bırakmaz. */}
              {flexrixStatus.wallet && Object.keys(flexrixStatus.wallet).length > 0 && (
                <div className="text-slate-400 mt-1">
                  Komisyon cüzdanı:{' '}
                  <b>{flexrixStatus.wallet.balance ?? flexrixStatus.wallet.amount ?? '—'}</b>
                  {' '}{flexrixStatus.wallet.currency || ''}
                  {' '}— boşalırsa sağlayıcı yeni bahsi kabul etmez
                  (MERCHANT_BALANCE_LOW) ve bize bildirim gelmez.
                </div>
              )}
              {flexrixStatus.wallet_error && (
                <div className="text-slate-500 mt-1">
                  Komisyon cüzdanı okunamadı: {flexrixStatus.wallet_error}
                  {' '}— bağlantı testini etkilemez.
                </div>
              )}
              {/* Oturum sınaması ayrı ölçülür: REST uçları açık olsa bile SSO
                  exchange reddediyorsa oyuncu spor kitabına giremez. Geçici
                  arıza ile kesin ret ayrı yazılır — ikisi karışırsa yönetici
                  bir dakikalık hıçkırığı kalıcı arıza sanar. */}
              {flexrixStatus.sso_ok === false && (
                <div className={`mt-1 ${flexrixStatus.sso_transient ? 'text-slate-400' : 'text-rose-400'}`}>
                  {flexrixStatus.sso_transient
                    ? `Oturum sınaması şu an yanıt vermedi (geçici): ${flexrixStatus.sso_error || '—'}`
                    : `Oyuncu oturumu kurulamıyor: ${flexrixStatus.sso_error || '—'}`}
                </div>
              )}
            </div>
          )}
          {rotationNote(flexrixRotation) && (
            <p className="gc-note is-info">{rotationNote(flexrixRotation)}</p>
          )}

          {/* Bu iki iş ayar değildir: ikisi de sağlayıcıda durur ve
              Partner API ile okunup yazılır — bu yüzden kaydet şeridinin
              dışındadırlar. */}
          <div className="gc-subblock">
            <div className="text-xs font-semibold text-slate-200">
              Ödenemeyen kazançlar ve maç durumu
            </div>
            <p className="text-[11px] text-slate-500 mt-1">
              Sonuç ödemesi cüzdanımıza ulaşamazsa sağlayıcı 18 kez dener
              (~43 saat), sonra <b>durur</b> ve kazanç oyuncuya hiç ödenmez.
              Yeniden kuyruğa almak tek çözümdür; ödeme aynı işlem kimliğiyle
              gittiği için çift ödeme olmaz.
            </p>
            <div className="gc-btns">
              <button
                type="button"
                onClick={loadFlexrixDead}
                disabled={flexrixBusy === 'dead'}
                className="gc-btn is-sm"
              >
                {flexrixBusy === 'dead'
                  ? 'Okunuyor…' : 'Ödenemeyen kazançları listele'}
              </button>
              <button
                type="button"
                onClick={loadFlexrixMatchPush}
                disabled={flexrixBusy === 'match'}
                className="gc-btn is-sm"
              >
                {flexrixBusy === 'match'
                  ? 'Okunuyor…' : 'Maç durumu bildirimini sorgula'}
              </button>
              {flexrixMatchPush !== null && (
                <button
                  type="button"
                  onClick={() => setFlexrixMatchPushValue(!flexrixMatchPush)}
                  disabled={flexrixBusy === 'match'}
                  className="gc-btn is-sm"
                >
                  {flexrixMatchPush
                    ? 'Skor bildirimini kapat' : 'Skor bildirimini aç'}
                </button>
              )}
            </div>
            {flexrixMatchPush !== null && (
              <p className="text-[11px] text-slate-400 mt-2">
                Maç durumu bildirimi{' '}
                <b>{flexrixMatchPush ? 'açık' : 'kapalı'}</b>. Açıkken
                sağlayıcı yalnızca <b>açık bahsimiz olan</b> maçların skorunu
                cüzdan callback'leriyle aynı imzayla gönderir; ayrı bir sır ya
                da adres alanı yoktur.
              </p>
            )}
            {flexrixDead !== null && (
              flexrixDead.length === 0 ? (
                <p className="text-[11px] text-emerald-400 mt-2">
                  Ödenemeyen kazanç yok.
                </p>
              ) : (
                <ul className="mt-2 space-y-1">
                  {flexrixDead.map((row) => (
                    <li
                      key={row.id}
                      className="flex items-center justify-between gap-2 text-[11px] text-slate-300"
                    >
                      <span>
                        #{row.id} · {row.payout ?? row.amount ?? '—'}{' '}
                        {row.currency || ''} · {row.credit_attempts ?? 0} deneme
                        {row.credit_last_error
                          ? ` · ${row.credit_last_error}` : ''}
                      </span>
                      <button
                        type="button"
                        onClick={() => retryFlexrixCredit(row.id)}
                        disabled={flexrixBusy === `retry:${row.id}`}
                        className="gc-btn is-sm"
                      >
                        {flexrixBusy === `retry:${row.id}`
                          ? 'Gönderiliyor…' : 'Yeniden kuyruğa al'}
                      </button>
                    </li>
                  ))}
                </ul>
              )
            )}
          </div>

          <SectionSave section="flexrix" />
        </Section>
        )}

        {showBook('emsports') && (
        <Section
          tone={TONES.emsports}
          icon={Trophy}
          title="EveryMatrix Sportsbook"
          subtitle="OddsMatrix oran akışı · para bizde, kupon sağlayıcıda"
          tags={['Hazır sportsbook', 'JWT launch', 'HMAC cüzdan']}
          badge={(
            <StatusBadge
              enabled={emsportsLive}
              warn={emsportsProviderDown}
              intent={
                emsports?.sports_enabled !== false && !!emsports?.enabled
              }
            />
          )}
          control={(
            <Switch
              tone={TONES.emsports}
              checked={
                emsports?.sports_enabled !== false && !!emsports?.enabled
              }
              onChange={(value) => updateEmsportsMany({
                sports_enabled: value,
                enabled: value,
              })}
              label="Spor kitabını yayına al / kaldır (kapalıyken sayfa siteden kalkar ve yeni bahis alınmaz)"
            />
          )}
          open={!!openSections.emsports}
          onToggle={() => toggleSection('emsports')}
          alert={emsportsAlert}
        >
          {!emsports ? (
            <p className="text-xs text-slate-500">
              EveryMatrix yapılandırması okunamadı. Sayfayı yenileyin.
            </p>
          ) : (
            <>
              <ServiceSwitchNote />
              <p className="text-xs text-slate-500 mb-3">
                Açıkken <code className="text-cyan-400">/spor</code> sayfası
                EveryMatrix çerçevesini çizer. <strong>Birden çok hazır spor
                kitabı aynı anda yayınlanabilir</strong> — o zaman oyuncu
                çerçevenin üstündeki logolu şeritten geçiş yapar; logoları
                aşağıdaki <b>Spor Kitabı Markaları</b> bölümünden verirsiniz.
              </p>
              <p className="text-xs text-slate-500 mb-3">
                Oyuncu imzalı bir jetonla sağlayıcının SPA'sına düşer; bahis,
                kazanç ve iptal <b>tek</b> uca (aşağıdaki bildirim adresi) HMAC
                imzalı olarak bildirilir ve üyenin finans listesinde görünür.
                Kupon içeriği (maçlar, oranlar) para hareketiyle birlikte
                geldiği için ayrı bir webhook kurmak gerekmez.
                <strong> Komisyon</strong> sonuçlanan her kuponun GGR'si
                üzerinden Sportsbook cüzdanınızdan düşer — bakiye biterse
                bahisler <code>merchant_out_of_funds</code> ile reddedilir.
              </p>
              <div className="gc-fields">
                <Field label="Spor kitabı adresi (SPA / iframe)">
                  <input
                    value={emsports.sports_url || ''}
                    onChange={(event) => updateEmsports('sports_url', event.target.value)}
                    className="gc-input"
                    placeholder="https://spor.siteniz.com"
                  />
                  <PanelDomainLeak host={emsports.sports_url_exposes_panel} />
                </Field>
                <Field label="Merchant Key">
                  <input
                    value={emsports.merchant_key || ''}
                    onChange={(event) => updateEmsports('merchant_key', event.target.value)}
                    className="gc-input"
                    placeholder="EM_XXXXXXXX"
                  />
                </Field>
                <Field label="Para Birimi (oyuncu cüzdanı)">
                  <input
                    value={emsports.currency || ''}
                    onChange={(event) => updateEmsports(
                      'currency', event.target.value.toUpperCase())}
                    className="gc-input"
                    placeholder="TRY"
                  />
                </Field>
                <Field label="SPA dili">
                  <select
                    value={emsports.language || 'tr'}
                    onChange={(event) => updateEmsports('language', event.target.value)}
                    className="gc-input"
                  >
                    {EMSPORTS_LANGUAGES.map((option) => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                </Field>
                <Field label="Tema">
                  <select
                    value={emsports.theme || 'dark'}
                    onChange={(event) => updateEmsports('theme', event.target.value)}
                    className="gc-input"
                  >
                    {EMSPORTS_THEMES.map((option) => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                </Field>
                <Field label="Jeton imza algoritması">
                  <select
                    value={emsports.jwt_algorithm || 'HS256'}
                    onChange={(event) => updateEmsports('jwt_algorithm', event.target.value)}
                    className="gc-input"
                  >
                    {EMSPORTS_JWT_ALGORITHMS.map((option) => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                </Field>
                <SecretField
                  label="API Secret"
                  value={emsports.api_secret}
                  configured={emsports.api_secret_configured}
                  placeholder="Cüzdan callback'lerinin ve oturum jetonunun anahtarı"
                  onChange={(value) => updateEmsports('api_secret', value)}
                />
              </div>

              {/* Bildirim adresi **sağlayıcının kendi panelindedir** ve istekte
                  gönderilmez; yazılmadan açılan kupon bize hiç bildirilmez. Tek
                  güvence yöneticinin onayıdır, bu yüzden onay kutusu olmadan
                  servis açılmaz (backend de 400 döner). */}
              <div className="gc-box is-tone">
                <div className="gc-box-head">
                  <div>
                    <h3>Bildirim (cüzdan) adresi</h3>
                    <p>Bahis, kazanç ve iptal bu tek uca imzalı olarak bildirilir.</p>
                  </div>
                </div>
                {callbackBaseMissing ? <CallbackBaseMissing /> : (
                  <div className="gc-copy">
                    <code className="gc-copy-url">{emsportsCallbackUrl}</code>
                    <button
                      type="button"
                      onClick={copyEmsportsCallback}
                      className="gc-btn is-sm"
                    >
                      {emsportsCopied ? <ClipboardCheck size={12} /> : 'Kopyala'}
                    </button>
                  </div>
                )}
                <p className="text-slate-400 mt-2">
                  Bu adres sağlayıcının panelinde <b>API Ayarları → EveryMatrix
                  Sportsbook</b> altına yazılır; istekte gönderilmez. Yazılmadan
                  entegrasyon açılamaz.
                </p>
                <label className="gc-check is-plain">
                  <input
                    type="checkbox"
                    checked={!!emsports.callback_registered}
                    onChange={(event) => updateEmsports(
                      'callback_registered', event.target.checked)}
                  />
                  <span>Adresi sağlayıcı paneline tanımladım</span>
                </label>
              </div>

              <div className="gc-checks">
                <label className="gc-check is-plain">
                  <input
                    type="checkbox"
                    checked={emsports.full_width !== false}
                    onChange={(event) => updateEmsports('full_width', event.target.checked)}
                  />
                  <span>Çerçeve tam genişlikte açılsın</span>
                </label>
                {/* Freebet'te sağlayıcı debit **hiç** göndermez, kazananı
                    yalnızca kâr tutarıyla bildirir. Kapı kapalıyken bahissiz
                    kazanç reddedilir: uydurma bir kupon numarasıyla kasadan
                    para çıkarmanın yolu olurdu. */}
                <label className="gc-check is-plain">
                  <input
                    type="checkbox"
                    checked={!!emsports.freebet_enabled}
                    onChange={(event) => updateEmsports('freebet_enabled', event.target.checked)}
                  />
                  <span>Freebet (bahissiz kazanç) kabul edilsin</span>
                </label>
              </div>

              {rotationNote(emsports) && (
                <p className="gc-note is-info">{rotationNote(emsports)}</p>
              )}

              {/* Şerit diğer bölümlerdekiyle aynı biçimdedir; ayrı olan tek
                  şey kaydın gittiği yerdir (`provider_configs`). */}
              <div className="gc-sect-save">
                <span className="gc-foot-note">
                  {emsportsDirty.length
                    ? `${emsportsDirty.length} alan değiştirildi, henüz kaydedilmedi.`
                    : 'Kaydedilmemiş değişiklik yok.'}
                </span>
                <button
                  type="button"
                  onClick={testEmsports}
                  disabled={emsportsBusy === 'test'}
                  className="gc-btn is-sm"
                >
                  {emsportsBusy === 'test'
                    ? <Loader2 size={13} className="gc-spin" />
                    : <RefreshCw size={13} />}
                  Bağlantıyı Test Et
                </button>
                <button
                  type="button"
                  onClick={saveEmsports}
                  disabled={emsportsBusy === 'save' || !emsportsDirty.length}
                  className="gc-btn is-sm is-primary"
                >
                  {emsportsBusy === 'save'
                    ? <Loader2 size={13} className="gc-spin" />
                    : <Save size={13} />}
                  Kaydet
                </button>
              </div>

              {emsportsStatus && (
                <div className="text-[11px] mt-2">
                  {emsportsStatus.connected ? (
                    <span className="text-emerald-400">
                      Bağlantı çalışıyor: merchant key ve imza kabul edildi.
                    </span>
                  ) : (
                    <span className="text-rose-400">
                      {/* Kenar katmanının (Cloudflare) reddi sağlayıcının
                          kararı **değildir**: istek uygulamaya hiç ulaşmamıştır.
                          "Anahtar yanlış" diye yazılırsa yönetici sağlayıcıya
                          yazar, oysa yapılacak iş bizdedir. */}
                      {emsportsStatus.error_code === 'IP_NOT_ALLOWED'
                        ? 'İstek sağlayıcının kenar katmanında kesildi (IP/UA). '
                        : ''}
                      {emsportsStatus.error || 'Bağlantı kurulamadı.'}
                    </span>
                  )}
                </div>
              )}
            </>
          )}
        </Section>
        )}

        {showBook('sportdigi') && (
        <Section
          tone={TONES.sportdigi}
          icon={Trophy}
          title="Digitain Sportsbook"
          subtitle="Digitain oran akışı · para bizde, kupon sağlayıcıda"
          tags={['Hazır sportsbook', 'JWT launch', 'HMAC cüzdan']}
          badge={(
            <StatusBadge
              enabled={sportdigiLive}
              warn={sportdigiProviderDown}
              intent={
                sportdigi?.sports_enabled !== false && !!sportdigi?.enabled
              }
            />
          )}
          control={(
            <Switch
              tone={TONES.sportdigi}
              checked={
                sportdigi?.sports_enabled !== false && !!sportdigi?.enabled
              }
              onChange={(value) => updateSportdigiMany({
                sports_enabled: value,
                enabled: value,
              })}
              label="Spor kitabını yayına al / kaldır (kapalıyken sayfa siteden kalkar ve yeni bahis alınmaz)"
            />
          )}
          open={!!openSections.sportdigi}
          onToggle={() => toggleSection('sportdigi')}
          alert={sportdigiAlert}
        >
          {!sportdigi ? (
            <p className="text-xs text-slate-500">
              Digitain yapılandırması okunamadı. Sayfayı yenileyin.
            </p>
          ) : (
            <>
              <ServiceSwitchNote />
              <p className="text-xs text-slate-500 mb-3">
                Açıkken <code className="text-orange-400">/spor</code> sayfası
                Digitain çerçevesini çizer. <strong>Birden çok hazır spor kitabı
                aynı anda yayınlanabilir</strong> — o zaman oyuncu çerçevenin
                üstündeki logolu şeritten geçiş yapar; logoları aşağıdaki
                <b> Spor Kitabı Markaları</b> bölümünden verirsiniz.
              </p>
              <p className="text-xs text-slate-500 mb-3">
                Spor kitabı adresi <b>kurulumun kendi alt alan adıdır</b>: DNS'te
                <code className="text-orange-400"> sportsdigi-edge.flexrix.com</code>
                {' '}adresine CNAME verilir (Cloudflare proxy açık) ve sertifika
                otomatik üretilir. Oyuncu imzalı bir jetonla SPA'ya düşer; bahis,
                kazanç ve iptal <b>tek</b> uca (aşağıdaki bildirim adresi) HMAC
                imzalı olarak bildirilir ve üyenin finans listesinde görünür.
                Kupon içeriği (maçlar, oranlar) para hareketiyle birlikte geldiği
                için ayrı bir webhook kurmak gerekmez.
                <strong> Komisyon</strong> her kabul edilen bahsin stake'i
                üzerinden Flexrix cüzdanınızdan düşer; bahis iptal edilirse iade
                edilir.
              </p>
              <div className="gc-fields">
                <Field label="Spor kitabı adresi (SPA / iframe)">
                  <input
                    value={sportdigi.sports_url || ''}
                    onChange={(event) => updateSportdigi('sports_url', event.target.value)}
                    className="gc-input"
                    placeholder="https://spor.siteniz.com"
                  />
                  <PanelDomainLeak host={sportdigi.sports_url_exposes_panel} />
                </Field>
                <Field label="Merchant Key">
                  <input
                    value={sportdigi.merchant_key || ''}
                    onChange={(event) => updateSportdigi('merchant_key', event.target.value)}
                    className="gc-input"
                    placeholder="SD_XXXXXXXX"
                  />
                </Field>
                <Field label="Para Birimi (oyuncu cüzdanı)">
                  <input
                    value={sportdigi.currency || ''}
                    onChange={(event) => updateSportdigi(
                      'currency', event.target.value.toUpperCase())}
                    className="gc-input"
                    placeholder="TRY"
                  />
                </Field>
                {/* Dil hem açılış adresine (`?lang=`) hem oturum jetonuna
                    yazılır; sağlayıcının sırası adres → jeton → markanın
                    varsayılanıdır. Küme kapalıdır: listedışı bir değer
                    sessizce markanın diline düşer. */}
                <Field label="Spor kitabı dili">
                  <select
                    value={sportdigi.language || 'tr'}
                    onChange={(event) => updateSportdigi('language', event.target.value)}
                    className="gc-input"
                  >
                    {SPORTDIGI_LANGUAGES.map((option) => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                </Field>
                {/* Boş bırakılırsa SPA kendi kökünü açar. */}
                <Field label="Açılış sayfası (isteğe bağlı)">
                  <input
                    value={sportdigi.return_path || ''}
                    onChange={(event) => updateSportdigi('return_path', event.target.value)}
                    className="gc-input"
                    placeholder="/SportsBook/Home"
                  />
                </Field>
                {/* Tema **sağlayıcıda** durur (`sportdigiTheme`): SPA onun
                    kaydından boyanır. Burada seçip kaydetmek uzun süre
                    hiçbir şey yapmıyordu; artık ayrı bir düğmeyle
                    sağlayıcıya yazılır ve kayıt ancak sağlayıcı kabul
                    ettiğinde güncellenir. */}
                <Field
                  label="Tema"
                  hint="Kaydet ile gitmez: sağlayıcıya ayrı bir çağrıyla uygulanır."
                >
                  <div className="gc-inline">
                    <select
                      value={sportdigi.theme || 'flexrix-red'}
                      onChange={(event) => updateSportdigi('theme', event.target.value)}
                      className="gc-input"
                    >
                      {SPORTDIGI_THEMES.map((option) => (
                        <option key={option.value} value={option.value}>{option.label}</option>
                      ))}
                    </select>
                    <button
                      type="button"
                      className="gc-btn is-sm"
                      disabled={sportdigiBusy === 'theme'}
                      onClick={applySportdigiTheme}
                    >
                      {sportdigiBusy === 'theme' ? 'Uygulanıyor…' : 'Temayı sağlayıcıya uygula'}
                    </button>
                  </div>
                </Field>
                <Field label="Jeton imza algoritması">
                  <select
                    value={sportdigi.jwt_algorithm || 'HS256'}
                    onChange={(event) => updateSportdigi('jwt_algorithm', event.target.value)}
                    className="gc-input"
                  >
                    {SPORTDIGI_JWT_ALGORITHMS.map((option) => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                </Field>
                <SecretField
                  label="API Secret"
                  value={sportdigi.api_secret}
                  configured={sportdigi.api_secret_configured}
                  placeholder="Cüzdan callback'lerinin ve oturum jetonunun anahtarı"
                  onChange={(value) => updateSportdigi('api_secret', value)}
                />
              </div>

              {/* Bildirim adresi **sağlayıcının kendi panelindedir** ve istekte
                  gönderilmez; yazılmadan açılan kupon bize hiç bildirilmez. Tek
                  güvence yöneticinin onayıdır, bu yüzden onay kutusu olmadan
                  servis açılmaz (backend de 400 döner). */}
              <div className="gc-box is-tone">
                <div className="gc-box-head">
                  <div>
                    <h3>Bildirim (cüzdan) adresi</h3>
                    <p>Bahis, kazanç ve iptal bu tek uca imzalı olarak bildirilir.</p>
                  </div>
                </div>
                {callbackBaseMissing ? <CallbackBaseMissing /> : (
                  <div className="gc-copy">
                    <code className="gc-copy-url">{sportdigiCallbackUrl}</code>
                    <button
                      type="button"
                      onClick={copySportdigiCallback}
                      className="gc-btn is-sm"
                    >
                      {sportdigiCopied ? <ClipboardCheck size={12} /> : 'Kopyala'}
                    </button>
                  </div>
                )}
                <p className="text-slate-400 mt-2">
                  Bu adres sağlayıcının panelinde <b>API Ayarları → Digitain</b>
                  {' '}altına yazılır; istekte gönderilmez. Yazılmadan
                  entegrasyon açılamaz.
                </p>
                <label className="gc-check is-plain">
                  <input
                    type="checkbox"
                    checked={!!sportdigi.callback_registered}
                    onChange={(event) => updateSportdigi(
                      'callback_registered', event.target.checked)}
                  />
                  <span>Adresi sağlayıcı paneline tanımladım</span>
                </label>
              </div>

              <div className="gc-checks">
                {/* Freebet'te sağlayıcı debit **hiç** göndermez, kazananı
                    yalnızca kâr tutarıyla bildirir. Kapı kapalıyken bahissiz
                    kazanç reddedilir: uydurma bir kupon numarasıyla kasadan
                    para çıkarmanın yolu olurdu. */}
                <label className="gc-check is-plain">
                  <input
                    type="checkbox"
                    checked={!!sportdigi.freebet_enabled}
                    onChange={(event) => updateSportdigi('freebet_enabled', event.target.checked)}
                  />
                  <span>Freebet (bahissiz kazanç) kabul edilsin</span>
                </label>
              </div>

              {rotationNote(sportdigi) && (
                <p className="gc-note is-info">{rotationNote(sportdigi)}</p>
              )}

              {/* Şerit diğer bölümlerdekiyle aynı biçimdedir; ayrı olan tek
                  şey kaydın gittiği yerdir (`provider_configs`). */}
              <div className="gc-sect-save">
                <span className="gc-foot-note">
                  {sportdigiDirty.length
                    ? `${sportdigiDirty.length} alan değiştirildi, henüz kaydedilmedi.`
                    : 'Kaydedilmemiş değişiklik yok.'}
                </span>
                <button
                  type="button"
                  onClick={testSportdigi}
                  disabled={sportdigiBusy === 'test'}
                  className="gc-btn is-sm"
                >
                  {sportdigiBusy === 'test'
                    ? <Loader2 size={13} className="gc-spin" />
                    : <RefreshCw size={13} />}
                  Bağlantıyı Test Et
                </button>
                <button
                  type="button"
                  onClick={loadSportdigiMatches}
                  disabled={sportdigiBusy === 'matches'}
                  className="gc-btn is-sm"
                >
                  {sportdigiBusy === 'matches'
                    ? <Loader2 size={13} className="gc-spin" />
                    : <RefreshCw size={13} />}
                  Öne Çıkan Maç Cevabı
                </button>
                <button
                  type="button"
                  onClick={saveSportdigi}
                  disabled={sportdigiBusy === 'save' || !sportdigiDirty.length}
                  className="gc-btn is-sm is-primary"
                >
                  {sportdigiBusy === 'save'
                    ? <Loader2 size={13} className="gc-spin" />
                    : <Save size={13} />}
                  Kaydet
                </button>
              </div>

              {sportdigiStatus && (
                <div className="text-[11px] mt-2">
                  {sportdigiStatus.connected ? (
                    <span className="text-emerald-400">
                      Bağlantı çalışıyor: merchant key ve imza kabul edildi.
                    </span>
                  ) : (
                    <span className="text-rose-400">
                      {/* Kenar katmanının (Cloudflare) reddi sağlayıcının
                          kararı **değildir**: istek uygulamaya hiç ulaşmamıştır.
                          "Anahtar yanlış" diye yazılırsa yönetici sağlayıcıya
                          yazar, oysa yapılacak iş bizdedir. */}
                      {sportdigiStatus.error_code === 'IP_NOT_ALLOWED'
                        ? 'İstek sağlayıcının kenar katmanında kesildi (IP/UA). '
                        : ''}
                      {sportdigiStatus.error || 'Bağlantı kurulamadı.'}
                    </span>
                  )}
                </div>
              )}

              {sportdigiMatches && (
                <div className="text-[11px] mt-2" data-testid="sportdigi-matches">
                  <p className="text-slate-400">
                    Çözümlenen maç: <b>{Array.isArray(sportdigiMatches.parsed)
                      ? sportdigiMatches.parsed.length : 0}</b>
                  </p>
                  <details>
                    <summary className="cursor-pointer text-slate-300">
                      Çözümlenmiş hâli
                    </summary>
                    <pre className="gc-raw-json">
                      {JSON.stringify(sportdigiMatches.parsed, null, 2)}
                    </pre>
                  </details>
                  <details>
                    <summary className="cursor-pointer text-slate-300">
                      Sağlayıcının ham cevabı
                    </summary>
                    <pre className="gc-raw-json">
                      {JSON.stringify(sportdigiMatches.raw, null, 2)}
                    </pre>
                  </details>
                </div>
              )}
            </>
          )}
        </Section>
        )}

        {showBook('exestruct') && (
        <Section
          tone={TONES.exestruct}
          icon={Trophy}
          title="Exestruct Sportsbook"
          subtitle="Swarm tabanlı spor kitabı · para bizde, kupon sağlayıcıda"
          tags={['Hazır sportsbook', 'SSO launch', 'HMAC webhook']}
          badge={(
            <StatusBadge
              enabled={exestructLive}
              warn={exestructProviderDown}
              intent={
                exestruct?.sports_enabled !== false && !!exestruct?.enabled
              }
            />
          )}
          control={(
            <Switch
              tone={TONES.exestruct}
              checked={
                exestruct?.sports_enabled !== false && !!exestruct?.enabled
              }
              onChange={(value) => setExestruct((current) => ({
                ...(current || {}),
                sports_enabled: value,
                enabled: value,
              }))}
              label="Spor kitabını yayına al / kaldır (kapalıyken sayfa siteden kalkar ve yeni bahis alınmaz)"
            />
          )}
          open={!!openSections.exestruct}
          onToggle={() => toggleSection('exestruct')}
          alert={exestructAlert}
        >
          {!exestruct ? (
            <p className="text-xs text-slate-500">
              Exestruct yapılandırması okunamadı. Sayfayı yenileyin.
            </p>
          ) : (
            <>
              <ServiceSwitchNote />
              <p className="text-xs text-slate-500 mb-3">
                Bilgiler Exestruct agent panelinde (<b>Ayarlar → API
                Bilgileri</b>) yazılıdır. Oyuncu spor sayfasını açtığında sunucu
                sağlayıcıdan bir oturum jetonu alır ve çerçeve o jetonla açılır;
                bahis, kazanç, iade ve bozdurma <b>tek</b> webhook adresine
                imzalı olarak bildirilir ve üyenin finans listesinde görünür.
                Kuponun maçları bahis geçmişinden okunur.
              </p>
              <div className="gc-fields">
                <Field label="Agent kodu (site_code)">
                  <input
                    value={exestruct.agent_code || ''}
                    onChange={(event) => updateExestruct('agent_code', event.target.value)}
                    className="gc-input"
                    name="exestruct_agent_code"
                    autoComplete="off"
                    spellCheck={false}
                    placeholder="siteniz123abc"
                  />
                </Field>
                <Field label="Para Birimi (oyuncu cüzdanı)">
                  <input
                    value={exestruct.currency || ''}
                    onChange={(event) => updateExestruct(
                      'currency', event.target.value.toUpperCase())}
                    className="gc-input"
                    placeholder="TRY"
                  />
                </Field>
                <Field label="Spor kitabı dili">
                  <select
                    value={exestruct.language || 'tr'}
                    onChange={(event) => updateExestruct('language', event.target.value)}
                    className="gc-input"
                  >
                    {EXESTRUCT_LANGUAGES.map((option) => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                </Field>
                <Field label="Oran biçimi">
                  <select
                    value={Number(exestruct.odd_type || 0)}
                    onChange={(event) => updateExestruct('odd_type', Number(event.target.value))}
                    className="gc-input"
                  >
                    {EXESTRUCT_ODD_TYPES.map((option) => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                </Field>
                <Field
                  label="Sağlayıcı adresi"
                  hint="Sağlayıcı başka bir alan adı vermedikçe değiştirmeyin."
                >
                  <input
                    value={exestruct.api_url || ''}
                    onChange={(event) => updateExestruct('api_url', event.target.value)}
                    className="gc-input"
                    name="exestruct_api_url"
                    type="url"
                    inputMode="url"
                    autoComplete="off"
                    spellCheck={false}
                    placeholder="https://sports.exestruct.com"
                  />
                </Field>
                <SecretField
                  label="API Key"
                  value={exestruct.api_key}
                  configured={exestruct.api_key_configured}
                  placeholder="ak_… — oturum ve bahis geçmişi çağrılarının anahtarı"
                  onChange={(value) => updateExestruct('api_key', value)}
                />
                <SecretField
                  label="API Secret"
                  value={exestruct.api_secret}
                  configured={exestruct.api_secret_configured}
                  placeholder="as_… — gelen webhook'un imza anahtarı"
                  onChange={(value) => updateExestruct('api_secret', value)}
                />
              </div>

              {/* Webhook adresi **sağlayıcının agent panelindedir** ve istekte
                  gönderilmez; yazılmadan açılan kupon bize hiç bildirilmez.
                  Onay kutusu olmadan servis açılmaz (backend de 400 döner). */}
              <div className="gc-box is-tone">
                <div className="gc-box-head">
                  <div>
                    <h3>Webhook adresi</h3>
                    <p>Bakiye, bahis, kazanç, iade ve bozdurma bu tek uca imzalı olarak gelir.</p>
                  </div>
                </div>
                {callbackBaseMissing ? <CallbackBaseMissing /> : (
                  <div className="gc-copy">
                    <code className="gc-copy-url">{exestructCallbackUrl}</code>
                    <button
                      type="button"
                      onClick={copyExestructCallback}
                      className="gc-btn is-sm"
                    >
                      {exestructCopied ? <ClipboardCheck size={12} /> : 'Kopyala'}
                    </button>
                  </div>
                )}
                <p className="text-slate-400 mt-2">
                  Bu adres Exestruct agent panelinde <b>Ayarlar → Webhook URL</b>
                  {' '}alanına yazılır; sağlayıcı türü sonuna <code>?type=</code>
                  {' '}ile ekler. Kaydettikten sonra oradaki <b>Test Gönder</b>
                  {' '}düğmesi <i>Webhook active</i> cevabını görmelidir.
                </p>
                <label className="gc-check is-plain">
                  <input
                    type="checkbox"
                    checked={!!exestruct.callback_registered}
                    onChange={(event) => updateExestruct(
                      'callback_registered', event.target.checked)}
                  />
                  <span>Adresi sağlayıcı paneline tanımladım</span>
                </label>
              </div>

              <div className="gc-sect-save">
                <span className="gc-foot-note">
                  {exestructDirty.length
                    ? `${exestructDirty.length} alan değiştirildi, henüz kaydedilmedi.`
                    : 'Kaydedilmemiş değişiklik yok.'}
                </span>
                <button
                  type="button"
                  onClick={testExestruct}
                  disabled={exestructBusy === 'test'}
                  className="gc-btn is-sm"
                >
                  {exestructBusy === 'test'
                    ? <Loader2 size={13} className="gc-spin" />
                    : <RefreshCw size={13} />}
                  Bağlantıyı Test Et
                </button>
                <button
                  type="button"
                  onClick={saveExestruct}
                  disabled={exestructBusy === 'save' || !exestructDirty.length}
                  className="gc-btn is-sm is-primary"
                >
                  {exestructBusy === 'save'
                    ? <Loader2 size={13} className="gc-spin" />
                    : <Save size={13} />}
                  Kaydet
                </button>
              </div>

              {exestructStatus && (
                <div className="text-[11px] mt-2" role="status">
                  {exestructStatus.connected ? (
                    <span className="text-emerald-400">
                      Bağlantı çalışıyor: API Key kabul edildi ve agent kodu eşleşiyor.
                    </span>
                  ) : (
                    <span className="text-rose-400">
                      {exestructStatus.error_code === 'IP_NOT_ALLOWED'
                        ? 'İstek sağlayıcının kenar katmanında kesildi (IP/UA). '
                        : ''}
                      {exestructStatus.error || 'Bağlantı kurulamadı.'}
                    </span>
                  )}
                </div>
              )}
            </>
          )}
        </Section>
        )}

        {showBook('viper') && (
        <Section
          tone={TONES.viper}
          icon={Trophy}
          title="Viper Sportsbook"
          subtitle="Viper GGR · para bizde, kupon sağlayıcıda"
          tags={['Hazır sportsbook', 'İmzalı launch', 'HMAC cüzdan']}
          badge={(
            <StatusBadge
              enabled={viperLive}
              warn={viperProviderDown}
              intent={viper?.sports_enabled !== false && !!viper?.enabled}
            />
          )}
          control={(
            <Switch
              tone={TONES.viper}
              checked={viper?.sports_enabled !== false && !!viper?.enabled}
              onChange={(value) => setViper((current) => ({
                ...(current || {}),
                sports_enabled: value,
                enabled: value,
              }))}
              label="Spor kitabını yayına al / kaldır (kapalıyken sayfa siteden kalkar ve yeni bahis alınmaz)"
            />
          )}
          open={!!openSections.viper}
          onToggle={() => toggleSection('viper')}
          alert={viperAlert}
        >
          {!viper ? (
            <p className="text-xs text-slate-500">
              Viper yapılandırması okunamadı. Sayfayı yenileyin.
            </p>
          ) : (
            <>
              <ServiceSwitchNote />
              <p className="text-xs text-slate-500 mb-3">
                Bilgiler Viper bayi panelinde (<b>Entegrasyon → Ayarlar ve
                anahtarlar</b>) yazılıdır. Sağlayıcı yalnızca beyaz listedeki
                sunucu IP&apos;lerinden gelen çağrıyı kabul eder; liste boşken
                hiçbir çağrı çalışmaz. Bahis, kazanç ve iade <b>tek</b> cüzdan
                adresine imzalı olarak bildirilir ve üyenin finans listesinde
                görünür. Spor kitabı yalnızca giriş yapmış üyeye açılır.
              </p>
              <div className="gc-fields">
                <Field
                  label="API adresi"
                  hint="Bayi hesabına özel adres (dokümandaki API base URL)."
                >
                  <input
                    value={viper.api_url || ''}
                    onChange={(event) => updateViper('api_url', event.target.value)}
                    className="gc-input"
                    name="viper_api_url"
                    type="url"
                    inputMode="url"
                    autoComplete="off"
                    spellCheck={false}
                    placeholder="https://….viperapiggr.site"
                  />
                </Field>
                <Field label="API Key">
                  <input
                    value={viper.api_key || ''}
                    onChange={(event) => updateViper('api_key', event.target.value.trim())}
                    className="gc-input"
                    name="viper_api_key"
                    autoComplete="off"
                    spellCheck={false}
                    placeholder="vk_…"
                  />
                </Field>
                <SecretField
                  label="API Secret"
                  value={viper.api_secret}
                  configured={viper.api_secret_configured}
                  placeholder="Panelde bir kez gösterilir — çağrıları ve cüzdan bildirimini imzalar"
                  onChange={(value) => updateViper('api_secret', value)}
                />
                <Field label="Para Birimi (oyuncu cüzdanı)">
                  <input
                    value={viper.currency || ''}
                    onChange={(event) => updateViper(
                      'currency', event.target.value.toUpperCase())}
                    className="gc-input"
                    placeholder="TRY"
                  />
                </Field>
                <Field label="Spor kitabı dili">
                  <select
                    value={viper.language || 'tr'}
                    onChange={(event) => updateViper('language', event.target.value)}
                    className="gc-input"
                  >
                    {VIPER_LANGUAGES.map((option) => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                </Field>
                <Field
                  label="Yayındaki spor kitapları"
                  hint="Birden çoğunu seçebilirsiniz; her biri oyuncunun spor sayfasında ayrı sekme olur."
                >
                  {viperBooks && viperBooks.length ? (
                    <div className="gc-checks">
                      {viperBooks.map((item) => (
                        <label key={item.id} className="gc-check is-plain">
                          <input
                            type="checkbox"
                            checked={(viper.books || []).some(
                              (book) => book.game_id === item.id)}
                            onChange={() => toggleViperBook(item)}
                          />
                          <span className="gc-check-text">
                            {item.name}
                            {item.maintenance ? ' (bakımda)' : ''}
                          </span>
                        </label>
                      ))}
                    </div>
                  ) : (
                    <div className="gc-copy">
                      <code className="gc-copy-url">
                        {(viper.books || []).map((book) => book.name || book.game_id)
                          .join(' · ') || 'Seçilmedi'}
                      </code>
                      <button
                        type="button"
                        onClick={loadViperBooks}
                        disabled={viperBusy === 'books'}
                        className="gc-btn is-sm"
                      >
                        {viperBusy === 'books'
                          ? <Loader2 size={12} className="gc-spin" />
                          : <RefreshCw size={12} />}
                        Spor kitaplarını getir
                      </button>
                    </div>
                  )}
                </Field>
              </div>

              {/* Cüzdan adresi sağlayıcının hesabındadır ve istekte
                  gönderilmez; yazılmadan açılan kupon bize hiç bildirilmez.
                  Yazma API ile yapılır, elle yazan yönetici onay kutusunu
                  işaretler (backend onay olmadan servisi açmaz). */}
              <div className="gc-box is-tone">
                <div className="gc-box-head">
                  <div>
                    <h3>Cüzdan adresi (callback_url)</h3>
                    <p>Bakiye, bahis, kazanç ve iade bu tek uca imzalı olarak gelir.</p>
                  </div>
                </div>
                {callbackBaseMissing ? <CallbackBaseMissing /> : (
                  <div className="gc-copy">
                    <code className="gc-copy-url">{viperCallbackUrl}</code>
                    <button
                      type="button"
                      onClick={copyViperCallback}
                      className="gc-btn is-sm"
                    >
                      {viperCopied ? <ClipboardCheck size={12} /> : 'Kopyala'}
                    </button>
                    <button
                      type="button"
                      onClick={registerViperCallback}
                      disabled={viperBusy === 'callback' || viperDirty.length > 0}
                      className="gc-btn is-sm"
                      title={viperDirty.length
                        ? 'Önce değişiklikleri kaydedin'
                        : 'Adresi Viper hesabına yazar; IP beyaz listesi korunur'}
                    >
                      {viperBusy === 'callback'
                        ? <Loader2 size={12} className="gc-spin" />
                        : <PlugZap size={12} />}
                      Sağlayıcıya kaydet
                    </button>
                  </div>
                )}
                <p className="text-slate-400 mt-2">
                  <b>Sağlayıcıya kaydet</b> adresi Viper hesabına API ile yazar.
                  Bunun için sunucunun çıkış IP&apos;si Viper panelindeki beyaz
                  listede olmalıdır. Kayıttan sonra Viper panelindeki
                  <b> Entegrasyon testi</b> iki adımı da geçmelidir.
                </p>
                <label className="gc-check is-plain">
                  <input
                    type="checkbox"
                    checked={!!viper.callback_registered}
                    onChange={(event) => updateViper(
                      'callback_registered', event.target.checked)}
                  />
                  <span>Adres Viper hesabında tanımlı</span>
                </label>
              </div>

              <div className="gc-sect-save">
                <span className="gc-foot-note">
                  {viperDirty.length
                    ? `${viperDirty.length} alan değiştirildi, henüz kaydedilmedi.`
                    : 'Kaydedilmemiş değişiklik yok.'}
                </span>
                <button
                  type="button"
                  onClick={testViper}
                  disabled={viperBusy === 'test'}
                  className="gc-btn is-sm"
                >
                  {viperBusy === 'test'
                    ? <Loader2 size={13} className="gc-spin" />
                    : <RefreshCw size={13} />}
                  Bağlantıyı Test Et
                </button>
                <button
                  type="button"
                  onClick={saveViper}
                  disabled={viperBusy === 'save' || !viperDirty.length}
                  className="gc-btn is-sm is-primary"
                >
                  {viperBusy === 'save'
                    ? <Loader2 size={13} className="gc-spin" />
                    : <Save size={13} />}
                  Kaydet
                </button>
              </div>

              {viperStatus && (
                <div className="text-[11px] mt-2" role="status">
                  {viperStatus.connected ? (
                    <span className={viperStatus.account?.can_play
                      ? 'text-emerald-400' : 'text-amber-400'}
                    >
                      Bağlantı çalışıyor · hesap {viperStatus.account?.status || '—'}
                      {' '}· bakiye {viperStatus.account?.balance_usd || '0'} USD
                      {viperStatus.account?.can_play
                        ? ''
                        : ' · Viper yeni açılış ve bahis kabul etmiyor (bakiye/kredi tükenmiş ya da hesap askıda)'}
                      {viperStatus.callback_url
                        ? ` · kayıtlı cüzdan adresi: ${viperStatus.callback_url}`
                        : ' · Viper hesabında cüzdan adresi yok'}
                    </span>
                  ) : (
                    <span className="text-rose-400">
                      {viperStatus.error || 'Bağlantı kurulamadı.'}
                    </span>
                  )}
                </div>
              )}
            </>
          )}
        </Section>
        )}

        {showBook('betby') && (
        <Section
          tone={TONES.betby}
          icon={Trophy}
          title="Betby Spor Kitabı"
          subtitle="SynCode · oran, kupon ve sonuçlandırma sağlayıcıda"
          tags={['Hazır sportsbook', 'Sağlayıcı kuponu']}
          badge={(
            <StatusBadge
              enabled={betbyLive}
              intent={
                !!settings.syncode_sports_enabled && !!settings.syncode_enabled
              }
            />
          )}
          control={(
            <Switch
              tone={TONES.betby}
              checked={
                !!settings.syncode_sports_enabled && !!settings.syncode_enabled
              }
              onChange={(value) => updateMany({
                syncode_sports_enabled: value,
                syncode_enabled: value,
              })}
              label="Spor kitabını yayına al / kaldır (kapalıyken sayfa siteden kalkar ve yeni bahis alınmaz)"
            />
          )}
          open={!!openSections.betby}
          onToggle={() => toggleSection('betby')}
          alert={betbyAlert}
        >
          <ServiceSwitchNote />
          <p className="text-xs text-slate-500 mb-3">
            Açıkken <code className="text-amber-400">/spor</code> sayfası Betby
            çerçevesini çizer; kapatıldığında sitenin kendi oran beslemesine geri
            döner. <strong>İkisi aynı anda yayınlanmaz</strong> — oyuncuya iki ayrı
            bahis dünyası göstermek kupon geçmişini de ikiye bölerdi.
          </p>
          <p className="text-xs text-slate-500 mb-3">
            Kupon Spor Bahisleri ekranında listelenmez: bize yalnızca para hareketi
            (bahis, kazanç, iptal) bildirilir ve bu hareketler üyenin finans
            listesinde görünür. Cashout, özel oran ve elle sonuçlandırma bu
            kaynakta çalışmaz; hepsi sağlayıcının kendi kurallarına tabidir.
          </p>
          <div className="gc-fields">
            <Field label="API URL" full>
              <input
                value={settings.syncode_api_url || ''}
                onChange={(event) => update('syncode_api_url', event.target.value)}
                className="gc-input"
                placeholder="https://api.syncode.win/api/casinoapi"
              />
            </Field>
            <Field label="Agent Code">
              <input
                value={settings.syncode_agent_code || ''}
                onChange={(event) => update('syncode_agent_code', event.target.value)}
                className="gc-input"
                placeholder="Agent kodu"
              />
            </Field>
            <SecretField
              label="API Token"
              value={settings.syncode_token}
              configured={settings.syncode_token_configured}
              placeholder="SynCode API token"
              onChange={(value) => update('syncode_token', value)}
            />
            <Field label="Para Birimi (agent hesabının para birimi)">
              <input
                value={settings.syncode_currency || ''}
                onChange={(event) => update(
                  'syncode_currency', event.target.value.toUpperCase())}
                className="gc-input"
                placeholder="TRY"
              />
            </Field>
            <Field label="Siteye dönüş adresi (homeUrl)">
              <input
                value={settings.syncode_home_url || ''}
                onChange={(event) => update('syncode_home_url', event.target.value)}
                className="gc-input"
                placeholder="https://site.com/"
              />
            </Field>
            <Field label="Betby düzeni">
              <select
                value={settings.syncode_betby_layout || 'tile'}
                onChange={(event) => update('syncode_betby_layout', event.target.value)}
                className="gc-input"
              >
                {BETBY_LAYOUTS.map((option) => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            </Field>
            <Field label="Betby renk şeması">
              <select
                value={settings.syncode_betby_color || 'black-green'}
                onChange={(event) => update('syncode_betby_color', event.target.value)}
                className="gc-input"
              >
                {BETBY_COLORS.map((option) => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            </Field>
          </div>

          <div className="gc-box is-tone">
            <div className="gc-box-head">
              <div>
                <h3>Site endpoint adresi</h3>
                <p>Sağlayıcı panelinde <b>Profile → Site endpoint</b> alanına yazılır.</p>
              </div>
            </div>
            {callbackBaseMissing ? <CallbackBaseMissing /> : (
              <div className="gc-copy">
                <code className="gc-copy-url">{betbyCallbackUrl}</code>
                <button
                  type="button"
                  onClick={copyBetbyCallback}
                  className="gc-btn is-sm"
                >
                  {betbyCopied ? 'Kopyalandı' : 'Kopyala'}
                </button>
              </div>
            )}
            <label className="gc-check">
              <input
                type="checkbox"
                checked={!!settings.syncode_callback_registered}
                onChange={(event) => update(
                  'syncode_callback_registered', event.target.checked)}
              />
              <span>
                Bu adresi SynCode panelinde Site endpoint alanına tanımladım
                <span className="block text-[11px] text-slate-500 mt-0.5">
                  Onaylanmadan servis açılamaz: adres yazılmazsa sağlayıcı bakiyeyi
                  bize hiç soramaz ve spor kitabı açılmaz. Kaydedilmiş onay
                  sonraki kayıtlarda korunur; sağlayıcı panelindeki kayıt artık
                  geçerli değilse kutuyu kaldırın.
                </span>
              </span>
            </label>
          </div>

          <SectionSave section="betby" />
        </Section>
        )}
      </div>

      {showRest && (
      <>
      <div className="gc-grouplabel">
        Oran kaynakları
        <span>Sitenin kendi spor sayfasını besleyen servisler; her bölümün alanları yalnızca kendi sağlayıcısına aittir.</span>
      </div>

      <div className="gc-sects">
        <Section
          tone={TONES.apifootball}
          icon={Globe}
          title="API-Football"
          subtitle="api-sports.io · futbol 1X2 oranı ve maç sonucu"
          tags={['Futbol 1X2', 'Maç sonucu']}
          badge={<StatusBadge enabled={!!settings.apifootball_enabled} />}
          control={(
            <Switch
              tone={TONES.apifootball}
              checked={!!settings.apifootball_enabled}
              onChange={(value) => update('apifootball_enabled', value)}
              label="API-Football beslemesini aç/kapat"
            />
          )}
          open={!!openSections.apifootball}
          onToggle={() => toggleSection('apifootball')}
          alert={missingKey('apifootball_enabled', 'apifootball_api_key')
            ? 'Anahtar girilmediği sürece bu kaynak açık olsa bile kapalı sayılır — kupon kabul edilip doğrulama anında hata vermesin diye.'
            : null}
        >
          <p className="text-xs text-slate-500 mb-3">
            Futbol 1X2 oranlarını ve <strong>maç sonuçlarını</strong> sağlar; bu
            kaynağın kuponları sonuç servisi üzerinden otomatik kapatılır.
          </p>
          <SecretField
            label="API-Football Anahtarı"
            value={settings.apifootball_api_key}
            configured={settings.apifootball_api_key_configured}
            placeholder="api-sports.io anahtarı"
            onChange={(value) => update('apifootball_api_key', value)}
          />
          <SectionSave section="apifootball" />
        </Section>

        <Section
          tone={TONES.sportsbook}
          icon={Radio}
          title="Sportsbook API"
          subtitle="RapidAPI · çoklu spor, sonuç servisi yok"
          tags={['Çoklu spor', 'Salt okunur']}
          badge={<StatusBadge enabled={!!settings.sportsbook_api_enabled} />}
          control={(
            <Switch
              tone={TONES.sportsbook}
              checked={!!settings.sportsbook_api_enabled}
              onChange={(value) => update('sportsbook_api_enabled', value)}
              label="Sportsbook oran beslemesini aç/kapat"
            />
          )}
          open={!!openSections.sportsbook}
          onToggle={() => toggleSection('sportsbook')}
          alert={missingKey('sportsbook_api_enabled', 'sportsbook_api_key')
            ? 'RapidAPI anahtarı girilmeden bu kaynağın ligleri vitrine düşmez.'
            : null}
        >
          <p className="text-xs text-slate-500 mb-3">
            Çoklu spor için moneyline, handikap, toplam ve canlı oran verir.{' '}
            <strong>Sonuç servisi yoktur</strong>; bu kaynağın kuponları aşağıdaki
            “Sonuç Bekleyen Olaylar” bölümünden elle kapatılır.
          </p>
          <div className="gc-fields">
            <SecretField
              label="RapidAPI Anahtarı"
              value={settings.sportsbook_api_key}
              configured={settings.sportsbook_api_key_configured}
              placeholder="x-rapidapi-key"
              onChange={(value) => update('sportsbook_api_key', value)}
            />
            <Field label="RapidAPI Host">
              <input
                value={settings.sportsbook_api_host || ''}
                onChange={(event) => update('sportsbook_api_host', event.target.value)}
                className="gc-input"
                placeholder="sportsbook-api2.p.rapidapi.com"
              />
            </Field>
            <Field label="API URL (opsiyonel)">
              <input
                value={settings.sportsbook_api_url || ''}
                onChange={(event) => update('sportsbook_api_url', event.target.value)}
                className="gc-input"
                placeholder="https://..."
              />
            </Field>
            <MarginField
              label="Sportsbook Oran Marjı (0.06 = %6)"
              value={settings.sportsbook_margin}
              fallback={0.06}
              onChange={(value) => update('sportsbook_margin', value)}
            />
            <DaySelect
              label="Sportsbook Maç Önü Tarih Aralığı"
              value={settings.sportsbook_upcoming_days}
              fallback={14}
              onChange={(value) => update('sportsbook_upcoming_days', value)}
            />
          </div>
          <SectionSave section="sportsbook" />
        </Section>

        <Section
          tone={TONES.oddsapi}
          icon={Zap}
          title="The Odds API"
          subtitle="the-odds-api.com · çoklu spor, skor servisi var"
          tags={['Çoklu spor', 'Skor ucu var']}
          badge={<StatusBadge enabled={!!settings.oddsapi_enabled} />}
          control={(
            <Switch
              tone={TONES.oddsapi}
              checked={!!settings.oddsapi_enabled}
              onChange={(value) => update('oddsapi_enabled', value)}
              label="The Odds API beslemesini aç/kapat"
            />
          )}
          open={!!openSections.oddsapi}
          onToggle={() => toggleSection('oddsapi')}
          alert={missingKey('oddsapi_enabled', 'oddsapi_api_key')
            ? 'API anahtarı girilmeden bu kaynağın ligleri vitrine düşmez.'
            : null}
        >
          <p className="text-xs text-slate-500 mb-3">
            Çoklu spor oranlarını farklı bahis şirketlerinden birleştirir.
            Sportsbook API’den farkı <strong>skor servisi vermesidir</strong>;
            otomatik sonuçlandırma açıksa bu kaynağın kuponları elle skor
            girilmeden kapanır.
          </p>
          <div className="gc-fields">
            <SecretField
              label="The Odds API Anahtarı"
              value={settings.oddsapi_api_key}
              configured={settings.oddsapi_api_key_configured}
              placeholder="the-odds-api.com anahtarı"
              onChange={(value) => update('oddsapi_api_key', value)}
            />
            <MarginField
              label="Odds API Oran Marjı (0.06 = %6)"
              value={settings.oddsapi_margin}
              fallback={0.06}
              onChange={(value) => update('oddsapi_margin', value)}
            />
            <DaySelect
              label="Odds API Maç Önü Tarih Aralığı"
              value={settings.oddsapi_upcoming_days}
              fallback={7}
              onChange={(value) => update('oddsapi_upcoming_days', value)}
            />
          </div>

          <div className="gc-fields">
            <div>
              <div className="gc-label">Marketler</div>
              <div className="gc-checks">
                {ODDSAPI_MARKETS.map((market) => (
                  <label key={market.code} className="gc-check is-plain">
                    <input
                      type="checkbox"
                      checked={oddsapiMarkets.includes(market.code)}
                      onChange={() => update(
                        'oddsapi_markets',
                        toggleCsv(
                          settings.oddsapi_markets || 'h2h,spreads,totals',
                          market.code,
                          ODDSAPI_MARKETS.map((item) => item.code),
                        ),
                      )}
                    />
                    {market.label}
                  </label>
                ))}
              </div>
            </div>
            <div>
              <div className="gc-label">Bahis şirketi bölgeleri</div>
              <div className="gc-checks">
                {ODDSAPI_REGIONS.map((region) => (
                  <label key={region.code} className="gc-check is-plain">
                    <input
                      type="checkbox"
                      checked={oddsapiRegions.includes(region.code)}
                      onChange={() => update(
                        'oddsapi_regions',
                        toggleCsv(
                          settings.oddsapi_regions || 'eu',
                          region.code,
                          ODDSAPI_REGIONS.map((item) => item.code),
                        ),
                      )}
                    />
                    {region.label}
                  </label>
                ))}
              </div>
            </div>
          </div>
          {/* Kota maliyeti panelde görünmezse market/bölge eklemenin bedeli
              ancak kredi bitince fark edilir. */}
          <p className="text-[11px] text-slate-500 mt-2">
            Lig başına bir olay isteği <strong>{oddsapiMarkets.length} × {oddsapiRegions.length} = {oddsapiMarkets.length * oddsapiRegions.length} kredi</strong> harcar.
            Market veya bölge eklemek maliyeti doğrudan katlar.
            {quota?.remaining != null && (
              <span className="text-emerald-400"> Kalan kredi: {Number(quota.remaining).toLocaleString('tr-TR')}.</span>
            )}
          </p>

          <label className="gc-check">
            <input
              type="checkbox"
              checked={settings.oddsapi_auto_settle !== false}
              onChange={(event) => update('oddsapi_auto_settle', event.target.checked)}
            />
            <span>
              Skorlardan otomatik sonuçlandır
              <span className="text-xs text-slate-500 block mt-0.5">
                Kapatılırsa bu kuponlar da aşağıdaki "Sonuç Bekleyen Olaylar"
                bölümünden elle sonuçlandırılır.
              </span>
            </span>
          </label>
          <SectionSave section="oddsapi" />
        </Section>
      </div>

      <div className="gc-grouplabel">
        Görünüm ve sıralama
        <span>Oyuncunun spor sayfasında gördüğü ad, logo ve sıra.</span>
      </div>

      <div className="gc-sects">
        <Section
          tone={TONES.neutral}
          icon={Images}
          title="Spor Kitabı Markaları"
          subtitle="Oyuncunun gördüğü geçiş şeridinin logoları ve adları"
          tags={['Görünüm']}
          open={!!openSections.brand}
          onToggle={() => toggleSection('brand')}
        >
          <p className="text-xs text-slate-500 mb-3">
            Birden çok hazır spor kitabı yayındayken çerçevenin üstünde logolu
            bir şerit çıkar ve oyuncu aralarında geçiş yapar. Logoyu
            <b>Yükle</b> düğmesiyle bilgisayarınızdan seçebilir ya da alana bir
            adres yapıştırabilirsiniz; yüklenen logo <b>anında kaydedilir</b>.
            Boş bırakılırsa sağlayıcının <b>varsayılan logosu</b> çizilir
            (EveryMatrix, Digitain ve Betby için hazır gelir); varsayılanı
            olmayan sağlayıcıda sekmede adı yazılır. Tek sağlayıcı yayındayken
            şerit hiç çizilmez.
          </p>
          <div className="gc-list">
            {branding.map((row) => (
              <div key={row.id} className="gc-row gc-brand-row">
                {/* Önizleme: yüklenen logonun gerçekten çizilip çizilmediği
                    ancak burada görülür. Adres göreli olabilir
                    (`/api/uploads/...`), bu yüzden `resolveApiAssetUrl`'den
                    geçer — ayrı origin'li kurulumda çözülmeyen adres 404 olur. */}
                <div className="gc-logo-preview">
                  {row.logo_url || defaultSportsbookLogo(row.id) ? (
                    <img
                      src={row.logo_url
                        ? resolveApiAssetUrl(row.logo_url)
                        : defaultSportsbookLogo(row.id)}
                      alt=""
                    />
                  ) : (
                    <span>logo yok</span>
                  )}
                </div>
                <Field label={`${row.default_label} · görünen ad`}>
                  <input
                    value={row.label || ''}
                    onChange={(event) => updateBrand(row.id, 'label', event.target.value)}
                    className="gc-input"
                    placeholder={row.default_label}
                  />
                </Field>
                <Field label="Logo adresi">
                  <div className="gc-inline">
                    <input
                      value={row.logo_url || ''}
                      onChange={(event) => updateBrand(row.id, 'logo_url', event.target.value)}
                      className="gc-input"
                      placeholder="/api/uploads/images/... veya https://..."
                    />
                    {/* Yüklenen logo hemen kaydedilir: alanı doldurup Kaydet'i
                        beklemek "yükledim ama sitede çıkmıyor" şikâyetinin en
                        kısa yoludur. */}
                    <label
                      className={`gc-btn gc-upload ${brandUploading === row.id ? 'is-busy' : ''}`}
                    >
                      {brandUploading === row.id
                        ? <Loader2 size={13} className="gc-spin" />
                        : <Upload size={13} />}
                      {brandUploading === row.id ? 'Yükleniyor…' : 'Yükle'}
                      <input
                        type="file"
                        accept="image/jpeg,image/png,image/gif,image/webp,image/svg+xml,.svg"
                        className="sr-only"
                        disabled={brandUploading === row.id}
                        onChange={(event) => uploadBrandLogo(row, event)}
                      />
                    </label>
                  </div>
                </Field>
                <button
                  type="button"
                  onClick={() => saveBrand(row)}
                  disabled={brandBusy === row.id}
                  className="gc-btn is-sm"
                >
                  {brandBusy === row.id
                    ? <Loader2 size={13} className="gc-spin" />
                    : <Save size={13} />}
                  Kaydet
                </button>
              </div>
            ))}
          </div>
        </Section>

        <Section
          icon={ListOrdered}
          title="Spor ve Lig Sıralaması"
          subtitle="Sitedeki varsayılan açılış sporu ve lig sırası"
          badge={<CountBadge>{orderedSports.length} spor · {competitions.length} lig</CountBadge>}
          open={!!openSections.order}
          onToggle={() => toggleSection('order')}
        >
          <p className="text-xs text-slate-500 mb-4">
            İlk spor ve o sporun ilk ligi sitede varsayılan açılış olur. Futbol
            başlangıçta ilk sıradadır. Spor dalı sırası iki oran kaynağı için
            <strong> ortaktır</strong>; lig sırası kaynak başına ayrı tutulur.
          </p>

          <div className="gc-label">Spor dalları (ortak)</div>
          <div className="gc-fields">
            {orderedSports.map((sport, index) => {
              const label = competitions.find((item) => item.sport === sport)?.sport_label || sport;
              return (
                <div key={sport} className="gc-row">
                  <span className="gc-row-index">{index + 1}</span>
                  <span className="gc-row-main">{label}</span>
                  <button type="button" onClick={() => moveCatalogItem('sportsbook_sport_order', sport, -1, availableSports)} disabled={index === 0} className="gc-btn is-icon is-sm" aria-label={`${label} yukarı taşı`}><ArrowUp size={12} /></button>
                  <button type="button" onClick={() => moveCatalogItem('sportsbook_sport_order', sport, 1, availableSports)} disabled={index === orderedSports.length - 1} className="gc-btn is-icon is-sm" aria-label={`${label} aşağı taşı`}><ArrowDown size={12} /></button>
                </div>
              );
            })}
            {orderedSports.length === 0 && (
              <div className="gc-empty">Henüz bağlı bir spor dalı yok.</div>
            )}
          </div>

          {availableFeeds.length > 1 && (
            <div className="gc-btns">
              {availableFeeds.map((feed) => (
                <button
                  key={feed}
                  type="button"
                  onClick={() => setCatalogFeed(feed)}
                  aria-pressed={activeFeed === feed}
                  className={`gc-btn is-sm ${activeFeed === feed ? 'is-tone' : ''}`}
                  style={{ '--gc-tone': '#38bdf8' }}
                >
                  {FEED_LABELS[feed] || feed}
                </button>
              ))}
            </div>
          )}
          <label className="gc-field">
            <span className="gc-label">Liglerini sırala</span>
            <select value={catalogSport} onChange={(event) => setCatalogSport(event.target.value)} className="gc-input">
              {orderedSports.map((sport) => (
                <option key={sport} value={sport}>{competitions.find((item) => item.sport === sport)?.sport_label || sport}</option>
              ))}
            </select>
          </label>
          <div className="gc-list is-scroll">
            {sportCompetitions.map((competition, index) => (
              <div key={`${competition.feed}:${competition.key}`} className="gc-row">
                <span className="gc-row-index">{index + 1}</span>
                <span className="gc-row-main">{competition.name}</span>
                <span className="gc-row-tag">{competition.short_name}</span>
                <button type="button" onClick={() => moveCatalogItem(feedOrderKey, competition.short_name, -1, availableCompetitionCodes, sportCompetitionCodes)} disabled={index === 0} className="gc-btn is-icon is-sm" aria-label={`${competition.name} yukarı taşı`}><ArrowUp size={12} /></button>
                <button type="button" onClick={() => moveCatalogItem(feedOrderKey, competition.short_name, 1, availableCompetitionCodes, sportCompetitionCodes)} disabled={index === sportCompetitions.length - 1} className="gc-btn is-icon is-sm" aria-label={`${competition.name} aşağı taşı`}><ArrowDown size={12} /></button>
              </div>
            ))}
            {sportCompetitions.length === 0 && (
              <div className="gc-empty">
                {competitions.length === 0
                  ? 'Lig listesi için bir oran kaynağını açıp anahtarı kaydedin, sonra sayfayı yenileyin.'
                  : 'Bu spor dalında seçili kaynağa ait lig yok.'}
              </div>
            )}
          </div>
          <SectionSave section="order" />
        </Section>

        <Section
          icon={Images}
          tone={logoStatus?.pending ? '#fbbf24' : TONES.neutral}
          title="Takım Logoları"
          subtitle="Oran kaynakları logo göndermez; adlar API-Football'dan çözülür"
          badge={logoStatus
            ? <CountBadge>{logoStatus.resolved} logo</CountBadge>
            : null}
          open={!!openSections.logos}
          onToggle={() => toggleSection('logos')}
        >
          <p className="text-xs text-slate-500">
            Vitrindeki her takım adı bir kez aranır ve bulunan logo kalıcı olarak
            saklanır — Spor sayfası açılırken sağlayıcıya hiç gidilmez. Logosu
            çözülmemiş takım, adının baş harflerinden üretilen renkli bir rozetle
            çizilir; liste hiçbir zaman ikonsuz kalmaz.
          </p>
          {!logoStatus?.available && (
            <p className="gc-note is-warn">
              Arama API-Football üzerinden yapılır. Anahtar girilmeden eşitleme
              çalışmaz; o hâlde tüm takımlar monogram rozetiyle görünür.
            </p>
          )}
          {logoStatus && (
            <div className="gc-mini">
              {[
                ['Çözüldü', logoStatus.resolved, '#34d399'],
                ['Kuyrukta', logoStatus.pending, '#fbbf24'],
                ['Bulunamadı', logoStatus.missing, '#94a3b8'],
              ].map(([label, value, color]) => (
                <div key={label} className="gc-mini-box">
                  <b style={{ color }}>{value}</b>
                  <span>{label}</span>
                </div>
              ))}
            </div>
          )}
          <div className="gc-btns">
            <button
              type="button"
              onClick={syncLogos}
              disabled={!!logoBusy || !logoStatus?.available}
              className="gc-btn"
            >
              {logoBusy === 'sync' ? <Loader2 size={14} className="gc-spin" /> : <RefreshCw size={14} />}
              {logoBusy === 'sync' ? 'Eşitleniyor' : 'Logoları Eşitle'}
            </button>
            <button
              type="button"
              onClick={retryMissingLogos}
              disabled={!!logoBusy || !logoStatus?.missing}
              className="gc-btn is-sm"
            >
              <RefreshCw size={12} /> Bulunamayanları Tekrar Dene
            </button>
            <button
              type="button"
              onClick={loadLogoStatus}
              className="gc-btn is-sm"
            >
              <RefreshCw size={12} /> Yenile
            </button>
          </div>
          {/* Kuyruk vitrin açıldıkça dolar; yönetici düğmeye bir kez basıp
              bitmediğini görünce nedenini bilmeli. */}
          <p className="text-[11px] text-slate-500 mt-2">
            Kuyruk, Spor sayfası her açıldığında yeni takım adlarıyla dolar. Bir
            tur en fazla 40 adı dener; kuyrukta kalan varsa düğmeye tekrar
            basın. Yalnızca futbol takımları aranır — API-Football başka dal
            kapsamaz.
          </p>
        </Section>
      </div>

      <div className="gc-grouplabel">
        Bahis kuralları ve araçlar
        <span>Cashout kuralı, elle müdahale, sonuç girişi ve bağlantı sınaması.</span>
      </div>

      <div className="gc-sects">
        <Section
          tone={TONES.cashout}
          icon={Banknote}
          title="Cashout"
          subtitle="Sistem özelliği · tüm spor kaynakları için geçerli"
          tags={['API değildir']}
          badge={<StatusBadge enabled={settings.cashout_enabled !== false} />}
          control={(
            <Switch
              tone={TONES.cashout}
              checked={settings.cashout_enabled !== false}
              onChange={(value) => update('cashout_enabled', value)}
              label="Cashout'u aç/kapat"
            />
          )}
          open={!!openSections.cashout}
          onToggle={() => toggleSection('cashout')}
        >
          <p className="text-xs text-slate-500 mb-3">
            Ayrı bir veri sağlayıcısı değildir. Aktif kupondaki seçimlerin güncel
            durumunu kendi spor kaynağından okuyup erken ödeme teklifini hesaplayan
            sistem özelliğidir.
          </p>
          <div className="gc-fields">
            <MarginField
              label="Cashout Kesintisi (0.08 = %8)"
              value={settings.cashout_margin}
              fallback={0.08}
              onChange={(value) => update('cashout_margin', value)}
            />
          </div>
          <p className="text-[11px] text-slate-500 mt-2">
            Hesaplanan adil değerden bu oranda pay düşülerek kullanıcıya teklif edilir.
          </p>
          <SectionSave section="cashout" />
        </Section>

        <Section
          icon={Wand2}
          tone={overrideCount ? '#fbbf24' : TONES.neutral}
          title="Özel Oranlar"
          subtitle="Seçili maçın oranına elle müdahale"
          badge={<CountBadge>{overrideCount} tanım</CountBadge>}
          open={!!openSections.odds}
          onToggle={() => toggleSection('odds')}
        >
          <SportsOddsOverrides
            competitions={competitions}
            onCountChange={setOverrideCount}
            onInspect={inspectOverride}
          />
        </Section>

        <Section
          icon={ClipboardCheck}
          tone={results.length ? '#fbbf24' : TONES.neutral}
          title="Sonuç Bekleyen Olaylar"
          subtitle="Sonuç servisi olmayan kaynakların kuponları burada kapatılır"
          badge={<CountBadge>{results.length} olay</CountBadge>}
          open={!!openSections.results}
          onToggle={() => toggleSection('results')}
        >
          <div className="gc-inline">
            <div className="text-xs text-slate-500 flex-1">
              Kaydedilen sonuç bekleyen kuponları anında sonuçlandırır; panelden
              girilen sonuç sağlayıcının skorunu ezer.
            </div>
            <button
              type="button"
              onClick={loadResults}
              className="gc-btn is-sm"
            >
              <RefreshCw size={12} /> Yenile
            </button>
          </div>
          {results.length === 0 ? (
            <div className="gc-empty">Sonuç girişi bekleyen book kuponu yok.</div>
          ) : (
            <div className="gc-list">
              {results.map((event) => (
                <div key={event.event_key} className="gc-row">
                  <div className="min-w-0 flex-1">
                    <div className="text-sm truncate">{event.name}</div>
                    <div className="text-[11px] text-slate-500">
                      {event.start_time ? new Date(event.start_time).toLocaleString('tr-TR') : ''} · {event.coupons} kupon
                      {event.provider && ` · ${FEED_LABELS[event.provider] || event.provider}`}
                    </div>
                  </div>
                  <input
                    type="number"
                    min="0"
                    placeholder={event.home || 'Ev'}
                    className="gc-input is-xs"
                    value={scores[event.event_key]?.home ?? ''}
                    onChange={(changeEvent) => setScores((current) => ({
                      ...current,
                      [event.event_key]: {
                        ...current[event.event_key],
                        home: changeEvent.target.value,
                      },
                    }))}
                  />
                  <input
                    type="number"
                    min="0"
                    placeholder={event.away || 'Dep'}
                    className="gc-input is-xs"
                    value={scores[event.event_key]?.away ?? ''}
                    onChange={(changeEvent) => setScores((current) => ({
                      ...current,
                      [event.event_key]: {
                        ...current[event.event_key],
                        away: changeEvent.target.value,
                      },
                    }))}
                  />
                  <button
                    type="button"
                    onClick={() => submitResult(event.event_key)}
                    disabled={resultBusy === event.event_key}
                    className="gc-btn is-sm is-primary"
                  >
                    {resultBusy === event.event_key && <Loader2 size={12} className="gc-spin" />}
                    Kaydet
                  </button>
                  <button
                    type="button"
                    onClick={() => submitResult(event.event_key, true)}
                    disabled={resultBusy === event.event_key}
                    className="gc-btn is-sm is-danger"
                  >
                    İptal (void)
                  </button>
                </div>
              ))}
            </div>
          )}
        </Section>

        <Section
          icon={Activity}
          title="Canlı Spor Beslemesi"
          subtitle="Bağlantıyı sınayın; maçlar veritabanına kopyalanmaz"
          badge={feedStatus
            ? <CountBadge>{feedStatus.competitions} lig bağlı</CountBadge>
            : null}
          open={!!openSections.feed}
          onToggle={() => toggleSection('feed')}
        >
          <div className="gc-inline">
            <div className="min-w-0 flex-1">
              <div className="text-xs text-slate-500">
                Maçlar veritabanına kopyalanmaz; Spor sayfası açıldığında API’den
                gelir ve 90 saniyede yenilenir.
              </div>
              {feedStatus && (
                <div className="text-xs text-emerald-400 mt-1">
                  {feedStatus.competitions} lig bağlı · {feedStatus.feed} · {feedStatus.competition}: {feedStatus.events} maç
                </div>
              )}
            </div>
            <button
              type="button"
              onClick={testFeed}
              disabled={feedBusy}
              className="gc-btn"
            >
              {feedBusy ? <Loader2 size={14} className="gc-spin" /> : <RefreshCw size={14} />}
              {feedBusy ? 'Besleme Kontrol Ediliyor' : 'Beslemeyi Test Et'}
            </button>
          </div>
        </Section>
      </div>
      </>
      )}

      </div>

      {/* Bölümler katlandığı için kaydet düğmesi listenin dibinde kalmamalı:
          hangi bölüm açık olursa olsun ekranda durur. */}
      <div className="gc-panel-foot is-sticky">
        <span className="gc-foot-note">
          {pendingTotal
            ? `${pendingTotal} ayar değiştirildi, henüz kaydedilmedi.`
            : 'Kaydedilmemiş değişiklik yok.'}
        </span>
        <button
          type="button"
          onClick={saveAll}
          disabled={savingAny || !pendingTotal}
          className="gc-btn is-primary"
        >
          {savingAny ? <Loader2 size={14} className="gc-spin" /> : <Save size={14} />}
          Spor Ayarlarını Kaydet
        </button>
      </div>
    </section>
  );
};

export default SportsControlPanel;
